import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useContext } from "react";
import { WalkthroughContext } from "../App";
import {
  safeGetJSON,
  safeSetJSON,
  safeGetItem,
  safeRemoveItem,
} from "../utils/localStorageUtils";
import RadialProgress from "../components/dashboard/radialProgressDashboard";
import PeakRecoveryProgress from "../components/dashboard/PeakRecoveryProgress";
import PeakScoreProgress from "../components/dashboard/PeakScoreProgress";
import Sparkline from "../components/dashboard/cards/Sparkline";
import {
  CalendarIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BellIcon,
  ClockIcon,
  PlusIcon,
  ArrowRightIcon,
  XMarkIcon,
  EyeIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon, PlayIcon } from "@heroicons/react/24/solid";
import DashboardAIInsightCard from "../components/dashboard/DashboardAIInsightCard";
import { useAuth } from "../contexts/AuthContext";
import { useFirebaseData } from "../hooks/useFirebaseData";
import { UserPublicData } from "../types/user";
import {
  generateDateID,
  addWellnessLogAndLinkToDayPost,
  calculateAndSavePeakScore,
  addLogIDToDayPost,
  addRecoveryLog,addNegativeInfluenceLog, 
  addNiggleLog,
  addNoteLog,
  addHealthLog,
  getDayPost,
  updateActivityLog,
} from "../services/dayPostService";
import {
  DayPost,
  WellnessLog,
  HealthLog,
  PeakScoreLog,
  ActivityLog,
  TrainingLog,
  RecoveryLog,
  Session,
} from "../types/post";
import { TrainingType } from "../types/activity";
import { sessionService } from "../services/sessionService";
import { trainingTypeService } from "../services/trainingTypeService";
import { asDisplayNameOrNull } from "../utils/displayName";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import AddWellnessLogForm from "../components/DayPost/AddWellnessLogForm";

import AddRecoveryLogForm from "../components/DayPost/AddRecoveryLogForm";
import RecoveryScheduleCard from "../components/schedule/RecoveryScheduleCard";
import {
  getScheduledRecoveries,
  linkScheduledRecovery,
} from "../services/scheduledRecoveryService";
import {
  ScheduledRecovery,
  isScheduledRecoveryLogged,
} from "../types/scheduledRecovery";
import AddNiggleLogForm from "../components/DayPost/AddNiggleLogForm";
import AddNoteLogForm from "../components/DayPost/AddNoteLogForm";
import AddHealthLogForm from "../components/DayPost/AddHealthLogForm";
import TrainingLogForm from "../components/DayPost/training-log/TrainingLogForm";
import PerformanceFlow from "../components/DayPost/performance/PerformanceFlow";
import AddLogModal, { type LogType } from "../components/DayPost/AddLogModal";
import ActivityLinkModal from "../components/dashboard/ActivityLinkModal";
import TrainingSessionCard from "../components/dashboard/cards/TrainingSessionCard";
import UnlinkedSessionsSection from "../components/dashboard/UnlinkedSessionsSection";
import EventsToLogSection from "../components/dashboard/EventsToLogSection";
import NextEventChip from "../components/dashboard/NextEventChip";
import DashboardBanners from "../components/dashboard/DashboardBanners";
import { PeakEvent } from "../types/event";
import SessionLinkModal from "../components/dashboard/SessionLinkModal";
import SessionBottomSheet from "../components/sessions/SessionBottomSheet";
import EditSessionModal from "../components/sessions/EditSessionModal";
import {
  canChangeSessionDate,
  toLocalDateString,
} from "../utils/sessionRules";
import RecoveryCard from "../components/dashboard/cards/RecoveryCard";

import {
  terraDailyService,
  TerraDailyDoc,
} from "../services/terraDailyService";
import { getFunctions, httpsCallable } from "firebase/functions";
import RecoveryLogCard from "../components/DayPost/RecoveryLogCard";
import WellnessDays from "../components/dashboard/WellnessDays";
import TrainingDays from "../components/dashboard/TrainingDays";
import { displayWorkloadFactor } from "../utils/loadBalanceDisplay";
import { parseAddLogParam } from "../utils/retention";
import SleepDays from "../components/dashboard/SleepDays";
import ActivityHeatmap from "../components/dashboard/ActivityHeatmap";
import StreakCard from "../components/dashboard/StreakCard";
import { useStreakStore } from "../stores/streakStore";
import { useHeatmapStore } from "../stores/heatmapStore";
import BottomNavBar from "../components/layout/BottomNav";
import DashboardExportDialog from "../components/dashboard/DashboardExportDialog";
import CurrentCycleCard from "../components/femaleCycle/CurrentCycleCard";
import { getCycleLogs, calculateCycleStats } from "../services/cycleService";
import { CycleStats } from "../types/cycle";
import { useDayPostList, useDayPostSubmission } from "../hooks/useDayPostData";
import { conversationService } from "../services/conversationService";
import { useSubscription } from "../providers/SubscriptionProvider";
import UpgradePricingCard from "../components/UpgradePricingCard";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  setDoc,
  doc,
  limit,
} from "firebase/firestore";
import { db } from "../firebase/config";
import Header from "../components/layout/Header";
import {
  calcSleepFromHealthLogs,
  formatSleepDuration,
  calcSleepFromTimeline,
} from "../utils";
import { calcSleepWithWellnessFallback } from "../utils";
import DashboardBackground from "../components/DashboardBackground";
import { useThemeStore } from "../stores/themeStore";
import OlyChat from "../components/OlyChat";
import AddNegativeInfluencesLogForm from "../components/DayPost/AddNegativeInfluencesLogForm";
// import AgentInput from "../components/AgentInput";

const Dashboard: React.FC = () => {
  const { startTour } = useContext(WalkthroughContext);
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Admin viewing: support /dashboard?uid={otherUserId}
  const queryUid = useMemo(
    () => new URLSearchParams(location.search).get("uid"),
    [location.search],
  );

  // If viewing another user's dashboard, fetch their public profile once
  const { data: viewedUser } = useFirebaseData<UserPublicData>({
    collection: "users",
    docId: queryUid || "",
    enabled: !!queryUid,
    maxAge: 600,
    enableRealtimeUpdates: true,
    fallbackToCache: true,
  });
  const [showWellnessForm, setShowWellnessForm] = useState<boolean>(false);

  const [showRecoveryForm, setShowRecoveryForm] = useState<boolean>(false);
  const [dayRecoveries, setDayRecoveries] = useState<ScheduledRecovery[]>([]);
  const [recoveryToLog, setRecoveryToLog] = useState<ScheduledRecovery | null>(
    null,
  );
  const [showNiggleForm, setShowNiggleForm] = useState<boolean>(false);
  const [linkedTrainingLogID, setLinkedTrainingLogID] = useState<string>("");
  const [showPerformanceForm, setShowPerformanceForm] =
    useState<boolean>(false);
  const [showNoteForm, setShowNoteForm] = useState<boolean>(false);
  const [showHealthForm, setShowHealthForm] = useState<boolean>(false);
  const [showTrainingFormNew, setShowTrainingFormNew] =
    useState<boolean>(false);
  // When set, the training log form opens pre-linked to this session (logging an
  // unlinked completed session from the dashboard).
  const [trainingFormSessionId, setTrainingFormSessionId] = useState<
    string | null
  >(null);
  // Session currently being linked to an existing training log (busy state).
  const [linkingSessionId, setLinkingSessionId] = useState<string | null>(null);
  // Session shown in the bottom sheet (tapped from "Sessions to log" or the
  // scheduled list), the one whose link modal is open, and the one whose date
  // is being changed (plan 3b / 3c).
  const [sheetSession, setSheetSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [linkModalSession, setLinkModalSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [dateChangeSession, setDateChangeSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [addLogModalOpen, setAddLogModalOpen] = useState<boolean>(false);
  const [showDesktopLogMenu, setShowDesktopLogMenu] = useState<boolean>(false);
  const [showOlyChat, setShowOlyChat] = useState<boolean>(false);
  const [showActivityLinkModal, setShowActivityLinkModal] =
    useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<
    (ActivityLog & { id: string }) | null
  >(null);
const [showNegativeInfluencesForm, setShowNegativeInfluencesForm] =
  useState<boolean>(false);
  // State for editing recovery logs
  const [editingRecoveryLog, setEditingRecoveryLog] = useState<{
    id: string;
    data: RecoveryLog;
    dateID: string;
  } | null>(null);

  // State for on-demand data fetching (for dates beyond cached range)
  const [onDemandData, setOnDemandData] = useState<DayPost | null>(null);
  const [onDemandLoading, setOnDemandLoading] = useState<boolean>(false);
  const [onDemandError, setOnDemandError] = useState<string | null>(null);

  // State for proposed sessions (coach-assigned sessions pending accept/reject)
  const [proposedSessions, setProposedSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const [proposedSessionsLoading, setProposedSessionsLoading] =
    useState<boolean>(false);
  const [acceptingSessionId, setAcceptingSessionId] = useState<string | null>(
    null,
  );
  const [rejectingSessionId, setRejectingSessionId] = useState<string | null>(
    null,
  );

  // State for scheduled sessions fetched from sessions collection (not in DayPost)
  const [scheduledSessions, setScheduledSessions] = useState<
    (Session & { id: string })[]
  >([]);

  // State for completed sessions fetched directly from the sessions collection
  // for the active date. Used as a fallback source so unlinked completed
  // sessions surface even when they aren't reflected in the cached DayPost.
  const [completedSessions, setCompletedSessions] = useState<
    (Session & { id: string })[]
  >([]);

  // State for session types (to show session type names)
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);

  // State for daily insight (removed - handled by AIInsightCard)
  const [learnMoreLoading, setLearnMoreLoading] = useState<boolean>(false);
  const [dismissedInsights, setDismissedInsights] = useState<Set<string>>(
    new Set(),
  );
  const [ignoringActivityKeys, setIgnoringActivityKeys] = useState<Set<string>>(
    new Set(),
  );

  // State for cycle tracking
  const [cycleStats, setCycleStats] = useState<CycleStats | null>(null);
  const [cycleLoading, setCycleLoading] = useState<boolean>(false);

  // State for dashboard info dialog
  const [showDashboardInfoDialog, setShowDashboardInfoDialog] =
    useState<boolean>(false);
  const [showDashboardExport, setShowDashboardExport] = useState(false);

  // State for 3-dot dropdown menu
  const [showDashboardMenu, setShowDashboardMenu] = useState<boolean>(false);

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = React.useRef(0);
  const isPulling = React.useRef(false);
  const PULL_THRESHOLD = 80;

  // State to track if daily insight is dismissed
  const [isDailyInsightDismissed, setIsDailyInsightDismissed] =
    useState<boolean>(false);

  // Subscription state
  const { isDefault, isPremium, isPlusOrHigher } = useSubscription();

  // Utility functions for localStorage
  const getDismissedInsightsFromStorage = useCallback((): Set<string> => {
    try {
      const parsed = safeGetJSON<string[]>("dismissedDailyInsights", []);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      console.error("Error loading dismissed insights from storage:", error);
    }
    return new Set();
  }, []);

  const saveDismissedInsightsToStorage = useCallback(
    (dismissedSet: Set<string>) => {
      try {
        safeSetJSON("dismissedDailyInsights", Array.from(dismissedSet));
      } catch (error) {
        console.error("Error saving dismissed insights to storage:", error);
      }
    },
    [],
  );

  // Auth context
  const { currentUser, userData, authState } = useAuth();
  const navigate = useNavigate();

  // Ref for the hidden date input
  const dateInputRef = React.useRef<HTMLInputElement>(null);

  // Load dismissed insights from localStorage on component mount
  useEffect(() => {
    const stored = getDismissedInsightsFromStorage();
    setDismissedInsights(stored);
  }, [getDismissedInsightsFromStorage]);

  // Check if daily insight is dismissed
  useEffect(() => {
    const todayDateID = generateDateID(new Date());
    const dismissedKey = `dismissed_insight_daily_${todayDateID}`;
    const wasDismissed = safeGetItem(dismissedKey) === "true";
    setIsDailyInsightDismissed(wasDismissed);
  }, []);
  useEffect(() => {
    if (userData?.deActivated) {
      navigate("/account-deactivated", { replace: true });
    }
  }, [userData, navigate]);

  // Use the same cached day posts hook as Feed page
  const {
    dayPosts,
    loading,
    error: dayPostsError,
    isFromCache,
    isOffline,
    fetchDayPostsData,
  } = useDayPostList({
    userId: queryUid || undefined,
    disableCache: !!queryUid,
  });

  // Use submission hook for adding logs
  const {
    submitting,
    error: submissionError,
    submitNoteLog,
    updateRecoveryLogById,
    deleteRecoveryLogById,
  } = useDayPostSubmission(async () => {
    // Fetch fresh data after successful submission
    await fetchDayPostsData(false);
  });

  // Fetch proposed sessions (coach-assigned) for the current user
  useEffect(() => {
    const fetchProposedSessions = async () => {
      const uid = queryUid || currentUser?.uid;
      if (!uid) return;

      try {
        setProposedSessionsLoading(true);
        const sessions = await sessionService.getProposedSessions(uid);
        // Only keep proposed sessions (not rejected)
        setProposedSessions(sessions.filter((s) => s.status === "proposed"));
      } catch (error) {
        console.error("Error fetching proposed sessions:", error);
      } finally {
        setProposedSessionsLoading(false);
      }
    };

    fetchProposedSessions();
  }, [currentUser?.uid, queryUid]);

  // Fetch training types for displaying session type names
  useEffect(() => {
    const fetchTrainingTypes = async () => {
      try {
        const types = await trainingTypeService.fetchTrainingTypes();
        setTrainingTypes(types);
      } catch (error) {
        console.error("Error fetching training types:", error);
      }
    };

    fetchTrainingTypes();
  }, []);

  const resolveSessionTypeName = useCallback(
    (session: {
      trainingTypeID?: string;
      sessionTypeID?: string;
    }): string | null => {
      const resolved = trainingTypeService.getSessionTrainingTypeName(
        session,
        trainingTypes,
      );
      if (resolved) return resolved;

      // Last-resort fallback for legacy composite IDs like `${sportId}:${name}`
      const raw = session.sessionTypeID?.trim();
      if (raw?.includes(":")) {
        const [, ...rest] = raw.split(":");
        const inferred = rest.join(":").trim();
        return inferred || null;
      }

      return null;
    },
    [trainingTypes],
  );

  // Handle accept proposed session
  const handleAcceptProposedSession = useCallback(
    async (sessionId: string, coachId: string) => {
      const uid = queryUid || currentUser?.uid;
      if (!uid) return;

      try {
        setAcceptingSessionId(sessionId);
        await sessionService.acceptProposedSession(uid, sessionId, coachId);

        // Flip status to planned so it moves from proposed to scheduled immediately
        setScheduledSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId ? { ...s, status: "planned" as const } : s,
          ),
        );
      } catch (error) {
        console.error("Error accepting proposed session:", error);
      } finally {
        setAcceptingSessionId(null);
      }
    },
    [currentUser?.uid, queryUid],
  );

  // Handle reject proposed session
  const handleRejectProposedSession = useCallback(
    async (sessionId: string, reason: string) => {
      const uid = queryUid || currentUser?.uid;
      if (!uid) return;

      try {
        setRejectingSessionId(sessionId);
        await sessionService.rejectProposedSession(uid, sessionId, reason);

        // Remove the rejected session from proposed list
        setProposedSessions((prev) => prev.filter((s) => s.id !== sessionId));
      } catch (error) {
        console.error("Error rejecting proposed session:", error);
      } finally {
        setRejectingSessionId(null);
      }
    },
    [currentUser?.uid, queryUid],
  );

  // Get current date from URL params or default to today
  const currentDate = React.useMemo(() => {
    const dateParam = searchParams.get("date");
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [searchParams]);

  const currentPlannedDate = React.useMemo(() => {
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${currentDate.getFullYear()}-${month}-${day}`;
  }, [currentDate]);

  const recoveryOwnerUid = queryUid || currentUser?.uid || "";
  const recoveryQueryKey = `${recoveryOwnerUid}|${currentPlannedDate}`;
  const [recoveryQueryKeySeen, setRecoveryQueryKeySeen] =
    useState(recoveryQueryKey);
  const recoveryQueryKeyRef = useRef(recoveryQueryKey);
  recoveryQueryKeyRef.current = recoveryQueryKey;
  // Drop the previous athlete or day during render, before paint, so that
  // assignment is gone while the next read is in flight.
  if (recoveryQueryKey !== recoveryQueryKeySeen) {
    setRecoveryQueryKeySeen(recoveryQueryKey);
    setDayRecoveries([]);
  }

  useEffect(() => {
    if (!recoveryOwnerUid) return;
    const requestKey = recoveryQueryKey;
    let cancelled = false;
    getScheduledRecoveries(
      recoveryOwnerUid,
      currentPlannedDate,
      currentPlannedDate,
    )
      .then((items) => {
        if (cancelled || recoveryQueryKeyRef.current !== requestKey) return;
        setDayRecoveries(items);
      })
      .catch((error) => {
        console.error("Error loading scheduled recovery:", error);
        if (cancelled || recoveryQueryKeyRef.current !== requestKey) return;
        setDayRecoveries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [recoveryOwnerUid, currentPlannedDate, recoveryQueryKey, showRecoveryForm]);
  const isDateBeyondCachedRange = useMemo(() => {
    const today = new Date();
    const daysDifference = Math.floor(
      (today.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    return daysDifference >= 7;
  }, [currentDate]);

  // Extract current day's data from cached dayPosts array or use on-demand data
  const dayPostData = useMemo(() => {
    if (isDateBeyondCachedRange) {
      return onDemandData;
    }
    const currentDateID = generateDateID(currentDate);
    return dayPosts.find((dayPost) => dayPost.dateID === currentDateID) || null;
  }, [dayPosts, currentDate, isDateBeyondCachedRange, onDemandData]);

  // Fallback to most recent cached day when offline/current day not found
  // const fallbackDayPost = useMemo(() => {
  //   if (dayPostData) return null;
  //   if (!dayPosts || dayPosts.length === 0) return null;
  //   const sorted = [...dayPosts].sort((a, b) =>
  //     b.dateID.localeCompare(a.dateID),
  //   );
  //   return sorted[0] || null;
  // }, [dayPostData, dayPosts]);
  const fallbackDayPost = useMemo(() => {
  if (dayPostData) return null;
  if (!dayPosts || dayPosts.length === 0) return null;
  const todayCheck = currentDate.toDateString() === new Date().toDateString();
  if (todayCheck && !isOffline) return null;
  const sorted = [...dayPosts].sort((a, b) =>
    b.dateID.localeCompare(a.dateID),
  );
  return sorted[0] || null;
}, [dayPostData, dayPosts, currentDate, isOffline]);

  const activeDayPost = dayPostData || fallbackDayPost || null;
  const isFallbackActive = !dayPostData && !!fallbackDayPost;

  const parseDateID = useCallback((dateID: string): Date => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6)) - 1;
    const day = parseInt(dateID.substring(6, 8));
    return new Date(year, month, day);
  }, []);

  const activeDate = useMemo(() => {
    return isFallbackActive && fallbackDayPost
      ? parseDateID(fallbackDayPost.dateID)
      : currentDate;
  }, [isFallbackActive, fallbackDayPost, currentDate, parseDateID]);

  // Fetch sessions from the sessions collection for the active date. This is a
  // fallback source for sessions that may not be reflected in the cached
  // DayPost yet: planned/executing sessions feed the "Scheduled Sessions"
  // section, and completed (but unlinked) sessions feed the "Sessions to log"
  // section so they reliably appear for whichever day is being viewed.
  const refreshSessionsForActiveDate = useCallback(async () => {
    const uid = queryUid || currentUser?.uid;
    if (!uid) return;

    try {
      const dateStr = `${activeDate.getFullYear()}-${String(activeDate.getMonth() + 1).padStart(2, "0")}-${String(activeDate.getDate()).padStart(2, "0")}`;
      const sessions = await sessionService.getSessionsForDateRange(
        uid,
        dateStr,
        dateStr,
      );
      setScheduledSessions(
        sessions.filter(
          (s) => s.status === "planned" || s.status === "executing",
        ),
      );
      setCompletedSessions(sessions.filter((s) => s.status === "complete"));
    } catch (error) {
      console.error("Error fetching sessions for active date:", error);
    }
  }, [currentUser?.uid, queryUid, activeDate]);

  useEffect(() => {
    refreshSessionsForActiveDate();
  }, [refreshSessionsForActiveDate]);

  // Convert error to string format expected by UI
  const error = dayPostsError ? dayPostsError.message : onDemandError;

  // Combined loading state - show loading if either cached or on-demand data is loading
  const combinedLoading = loading || onDemandLoading;

  // Calculate scores from day post data
  const calculateWellnessScore = useCallback(
    (wellnessLogs: WellnessLog[]): number => {
      if (wellnessLogs.length === 0) return 0;

      // Get the most recent wellness log
      const latestLog = wellnessLogs[wellnessLogs.length - 1];
      return latestLog.wellnessScore || 0;
    },
    [],
  );

  const calculatePeakScore = useCallback(
    (peakScoreLog?: PeakScoreLog): number => {
      return peakScoreLog?.peakScore || 0;
    },
    [],
  );

  const calculateSleepDuration = useCallback(
    (healthLogs: HealthLog[], wellnessLogs: any[]): number => {
      return calcSleepWithWellnessFallback(healthLogs, wellnessLogs);
    },
    [],
  );

  // Navigation functions
  const goToPreviousDay = useCallback(() => {
    const previousDay = new Date(currentDate);
    previousDay.setDate(previousDay.getDate() - 1);
    const dateID = generateDateID(previousDay);
    const newParams = { date: dateID };
    if (queryUid) {
      (newParams as any).uid = queryUid;
    }
    setSearchParams(newParams);
  }, [currentDate, setSearchParams, queryUid]);

  const goToNextDay = useCallback(() => {
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const dateID = generateDateID(nextDay);
    const newParams = { date: dateID };
    if (queryUid) {
      (newParams as any).uid = queryUid;
    }
    setSearchParams(newParams);
  }, [currentDate, setSearchParams, queryUid]);

  const goToToday = useCallback(() => {
    const today = new Date();
    const dateID = generateDateID(today);
    const newParams = { date: dateID };
    if (queryUid) {
      (newParams as any).uid = queryUid;
    }
    setSearchParams(newParams);
  }, [setSearchParams, queryUid]);

  // Handle date picker
  const handleOpenDatePicker = useCallback(() => {
    console.log("Calendar icon clicked, attempting to open date picker");
    if (dateInputRef.current) {
      console.log("Date input ref found, triggering click");
      // Try multiple approaches to trigger the date picker
      try {
        dateInputRef.current.focus();
        dateInputRef.current.click();

        // Alternative approach: dispatch click event
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        dateInputRef.current.dispatchEvent(clickEvent);

        // Another approach: show picker if available
        if ("showPicker" in dateInputRef.current) {
          (dateInputRef.current as any).showPicker();
        }
      } catch (error) {
        console.error("Error triggering date picker:", error);
      }
    } else {
      console.error("Date input ref not found");
    }
  }, []);

  const handleDateSelect = useCallback(
    (selectedDate: string) => {
      if (!selectedDate) return;
      // selectedDate is in YYYY-MM-DD format from input[type="date"]
      const date = new Date(selectedDate);
      const dateID = generateDateID(date);
      const newParams = { date: dateID };
      if (queryUid) {
        (newParams as any).uid = queryUid;
      }
      setSearchParams(newParams);
    },
    [setSearchParams, queryUid],
  );

  // Navigate to wellness insights
  const goToWellnessInsights = useCallback(() => {
    const dateID = generateDateID(currentDate);
    const url = queryUid
      ? `/insights/wellness?date=${dateID}&uid=${queryUid}`
      : `/insights/wellness?date=${dateID}`;
    navigate(url);
  }, [currentDate, navigate, queryUid]);

  // Navigate to sleep insights
  const goToSleepInsights = useCallback(() => {
    const dateID = generateDateID(currentDate);
    const url = queryUid
      ? `/insights/sleep?date=${dateID}&uid=${queryUid}`
      : `/insights/sleep?date=${dateID}`;
    navigate(url);
  }, [currentDate, navigate, queryUid]);

  // Navigate to peak insights
  const goToPeakInsights = useCallback(() => {
    const dateID = generateDateID(currentDate);
    const url = queryUid
      ? `/insights/peak?date=${dateID}&uid=${queryUid}`
      : `/insights/peak?date=${dateID}`;
    navigate(url);
  }, [currentDate, navigate, queryUid]);

  // Navigate to training insights
  const goToTrainingInsights = useCallback(() => {
    const dateID = generateDateID(currentDate);
    const url = queryUid
      ? `/insights/training?date=${dateID}&uid=${queryUid}`
      : `/insights/training?date=${dateID}`;
    navigate(url);
  }, [currentDate, navigate, queryUid]);

  const goToDailyInsights = useCallback(() => {
    const dateID = generateDateID(currentDate);
    const url = queryUid
      ? `/insights/daily?date=${dateID}&uid=${queryUid}`
      : `/insights/daily?date=${dateID}`;
    navigate(url);
  }, [currentDate, navigate, queryUid]);

  // Handle learn more button click to start a new conversation
  const handleLearnMoreInsight = useCallback(async () => {
    if (!currentUser?.uid || learnMoreLoading) return;

    setLearnMoreLoading(true);
    try {
      const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;

      let contextRef;
      let contextId;

      // Check for existing valid context (reuse logic from Chats.tsx)
      const existingContextsQuery = query(
        collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        where("isActive", "==", true),
        orderBy("createdAt", "desc"),
        limit(1),
      );

      const existingContextsSnapshot = await getDocs(existingContextsQuery);

      if (!existingContextsSnapshot.empty) {
        const latestContext = existingContextsSnapshot.docs[0];
        const contextData = latestContext.data();
        const contextAge = Date.now() - contextData.createdAt.toMillis();
        const contextDataRange = contextData.context?.summary?.dateRange?.from;

        // Check if context is still valid (less than 1 hour old AND covers the same time range)
        const isContextValid =
          contextAge < 60 * 60 * 1000 &&
          contextDataRange &&
          contextDataRange >= fourteenDaysAgo;

        if (isContextValid) {
          console.log("Using existing valid context");
          contextId = latestContext.id;
          contextRef = { id: contextId };
        }
      }

      if (!contextRef) {
        // Create new context with current user's data
        console.log("Creating new context");

        // Use the same data that would be used for insights
        const wellnessData = dayPosts
          .filter((post) => post.wellnessLogs && post.wellnessLogs.length > 0)
          .map((post) => ({
            date: post.dateID,
            wellness: post.wellnessLogs[post.wellnessLogs.length - 1],
          }));

        const trainingData = dayPosts
          .filter((post) => post.trainingLogs && post.trainingLogs.length > 0)
          .map((post) => ({
            date: post.dateID,
            training: post.trainingLogs,
          }));

        const chatContext = {
          wellnessData: wellnessData,
          trainingData: trainingData,
          summary: {
            totalWellnessEntries: wellnessData.length,
            totalTrainingEntries: trainingData.length,
            dateRange: {
              from: fourteenDaysAgo,
              to: Date.now(),
            },
          },
        };

        // Save context document
        contextRef = doc(
          collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        );
        await setDoc(contextRef, {
          context: chatContext,
          createdAt: serverTimestamp(),
          isActive: true,
        });

        contextId = contextRef.id;
      }

      // Create new conversation with daily insight as initial context
      const conversationsRef = collection(
        db,
        `wizardConversations/${currentUser.uid}/conversations`,
      );
      const firstMessage = "Analyse my daily data";
      const conversationDoc = await addDoc(conversationsRef, {
        title: "Daily Insight Discussion",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        firstMessage: firstMessage,
        contextId: contextId,
      });

      // Save the conversation history in the correct order:
      // 1. User: "Analyse my daily data"
      await conversationService.saveMessage(
        currentUser.uid,
        conversationDoc.id,
        {
          role: "user",
          content: firstMessage,
          timestamp: new Date(),
        },
      );

      // 2. Gemini: placeholder for daily insight (will be handled by chat)
      await conversationService.saveMessage(
        currentUser.uid,
        conversationDoc.id,
        {
          role: "model",
          content: "Let me analyze your daily data and provide insights.",
          timestamp: new Date(),
        },
      );

      // 3. User: "Tell me more about my daily insight"
      await conversationService.saveMessage(
        currentUser.uid,
        conversationDoc.id,
        {
          role: "user",
          content: "Tell me more about my daily insight",
          timestamp: new Date(),
        },
      );

      // Navigate to the new conversation
      navigate(`/chats/${conversationDoc.id}`);
    } catch (error) {
      console.error("Error creating conversation:", error);
    } finally {
      setLearnMoreLoading(false);
    }
  }, [currentUser?.uid, dayPosts, navigate, learnMoreLoading]);

  // Handle dismissing daily insight for current day
  const handleDismissInsight = useCallback(() => {
    const todayDateID = generateDateID(new Date());
    setDismissedInsights((prev) => {
      const newSet = new Set(Array.from(prev).concat(todayDateID));
      saveDismissedInsightsToStorage(newSet);
      return newSet;
    });
    setIsDailyInsightDismissed(true);
  }, [saveDismissedInsightsToStorage]);

  // Handle showing dismissed daily insight
  const handleShowDailyInsight = useCallback(() => {
    const todayDateID = generateDateID(new Date());
    const dismissedKey = `dismissed_insight_daily_${todayDateID}`;
    safeRemoveItem(dismissedKey);
    setIsDailyInsightDismissed(false);
  }, []);

  // Navigate to specific training log insights
  const goToTrainingLogInsights = useCallback(
    (trainingLogId: string) => {
      const url = queryUid
        ? `/insights/trainingLog/${trainingLogId}?uid=${queryUid}`
        : `/insights/trainingLog/${trainingLogId}`;
      navigate(url);
    },
    [navigate, queryUid],
  );

  // Handle wellness form
  const handleOpenWellnessForm = useCallback(() => {
    setShowWellnessForm(true);
  }, []);

  // ── Cache invalidation after log submission ──────────────────────────────
  // Clears BOTH the streak cache and the heatmap cache so both cards
  // reflect the new log immediately without waiting for the 1-hour TTL.
  const { clearCache: clearStreakCache } = useStreakStore();
  const { clearYearCache } = useHeatmapStore();
  const invalidateStreakCache = useCallback(() => {
    const uid = currentUser?.uid;
    if (!uid) return;
    // Bust streak
    clearStreakCache(uid);
    // Bust heatmap for current year (the only year a new log can affect)
    clearYearCache(uid, new Date().getFullYear());
  }, [currentUser?.uid, clearStreakCache, clearYearCache]);

  // Floating Add button handlers
  const handleOpenAddLogModal = useCallback(() => {
    setShowDesktopLogMenu((prev) => !prev);
  }, []);

  const handleCloseAddLogModal = useCallback(() => {
    setAddLogModalOpen(false);
  }, []);

  const handleSelectLogType = useCallback((logType: LogType) => {
    setAddLogModalOpen(false);
    setShowDesktopLogMenu(false);
    switch (logType) {
      case "training":
        setTrainingFormSessionId(null);
        setShowTrainingFormNew(true);
        break;
      case "wellness":
        setShowWellnessForm(true);
        break;
      case "recovery":
        setShowRecoveryForm(true);
        break;
      case "negativeInfluences":
        setShowNegativeInfluencesForm(true);
        break;  
      case "niggle":
        setShowNiggleForm(true);
        break;
      case "performance":
        setShowPerformanceForm(true);
        break;
      case "note":
        setShowNoteForm(true);
        break;
      case "health":
        setShowHealthForm(true);
        break;
      default:
        // console.warn(
        //   "[Dashboard] AddLogModal log type not handled here:",
        //   logType,
        // );
    }
  }, []);

  // Deep link from banner buttons and push notifications: /dashboard?addLog=wellness|training|recovery|niggle
  useEffect(() => {
    if (!searchParams.has("addLog")) return;
    const logType = parseAddLogParam(searchParams.get("addLog"));
    if (logType && !queryUid) handleSelectLogType(logType);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("addLog");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, queryUid, handleSelectLogType, setSearchParams]);

  // Function to fetch on-demand data for dates beyond cached range
  const fetchOnDemandData = useCallback(
    async (date: Date) => {
      const effectiveUserId = queryUid || currentUser?.uid;
      if (!effectiveUserId) {
        setOnDemandLoading(false);
        return;
      }

      try {
        setOnDemandLoading(true);
        setOnDemandError(null);

        const dateID = generateDateID(date);
        const dayPost = await getDayPost(effectiveUserId, dateID);

        setOnDemandData(dayPost);
      } catch (err) {
        console.error("Error fetching on-demand day data:", err);
        setOnDemandError("Failed to load day data");
        setOnDemandData(null);
      } finally {
        setOnDemandLoading(false);
      }
    },
    [currentUser?.uid, queryUid],
  );

  const handleCloseWellnessForm = useCallback(() => {
    setShowWellnessForm(false);
  }, []);

  const handleWellnessSubmit = useCallback(
    async (wellnessLog: any) => {
      if (!currentUser?.uid) return;
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      const dateID = generateDateID(currentDate);
      const targetDate = currentDate;
      const beyondCachedRange = isDateBeyondCachedRange;

      try {
        // Save the wellness log and link it to the day post. This is fast, so
        // we can close the form and return the user to their previous page
        // without waiting on the peak score calculation (a slow Cloud Function
        // call). The peak score is then calculated in the background and the
        // UI is refreshed again once it finishes.
        const result = await addWellnessLogAndLinkToDayPost(
          currentUser.uid,
          wellnessLog,
          dateID,
        );

        // console.log("Wellness log added successfully:", result);

        // Close the form immediately
        setShowWellnessForm(false);

        // Invalidate streak cache so StreakCard reflects this new log immediately
        invalidateStreakCache();

        // Refresh the data (cached or on-demand) so the wellness log shows up
        if (beyondCachedRange) {
          await fetchOnDemandData(targetDate);
        } else {
          await fetchDayPostsData(false);
        }

        // Kick off peak score calculation in the background. When it finishes,
        // refresh the data again so the updated peak score appears in the UI.
        void (async () => {
          try {
            const peakScoreResult = await calculateAndSavePeakScore(dateID);
            if (!peakScoreResult.success) {
              console.warn(
                "Peak score calculation failed (background):",
                peakScoreResult.error,
              );
            } else {
              // console.log(
              //   "Peak score calculated (background):",
              //   peakScoreResult.data,
              // );
            }

            if (beyondCachedRange) {
              await fetchOnDemandData(targetDate);
            } else {
              await fetchDayPostsData(false);
            }
          } catch (bgErr) {
            console.error("Background peak score calculation failed:", bgErr);
          }
        })();
      } catch (error) {
        console.error("Error adding wellness log:", error);
        // You might want to show an error message to the user here
      }
    },
    [
      currentUser?.uid,
      queryUid,
      currentDate,
      fetchDayPostsData,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      invalidateStreakCache,
    ],
  );
const handleOpenNegativeInfluencesForm = useCallback(() => {
  setShowNegativeInfluencesForm(true);
}, []);

const handleCloseNegativeInfluencesForm = useCallback(() => {
  setShowNegativeInfluencesForm(false);
}, []);
  // Recovery form handlers
  const handleOpenRecoveryForm = useCallback(() => {
    setRecoveryToLog(null);
    setShowRecoveryForm(true);
  }, []);

  const handleCloseRecoveryForm = useCallback(() => {
    setShowRecoveryForm(false);
    setEditingRecoveryLog(null);
    setRecoveryToLog(null);
  }, []);

  // Niggle form handlers
  const handleOpenNiggleForm = useCallback(() => {
    setShowNiggleForm(true);
  }, []);

  const handleCloseNiggleForm = useCallback(() => {
    setShowNiggleForm(false);
    setLinkedTrainingLogID(""); // Clear the linked training log ID when closing
  }, []);

  const handleTriggerNiggleForm = useCallback((trainingLogId: string) => {
    setLinkedTrainingLogID(trainingLogId);
    setShowNiggleForm(true);
  }, []);

  // Performance form handlers
  const handleOpenPerformanceForm = useCallback(() => {
    setShowPerformanceForm(true);
  }, []);

  const handleClosePerformanceForm = useCallback(() => {
    setShowPerformanceForm(false);
  }, []);

  // Note form handlers
  const handleOpenNoteForm = useCallback(() => {
    setShowNoteForm(true);
  }, []);

  const handleCloseNoteForm = useCallback(() => {
    setShowNoteForm(false);
  }, []);

  const handleCloseHealthForm = useCallback(() => {
    setShowHealthForm(false);
  }, []);

  // Training form handlers
  const handleOpenTrainingForm = useCallback(() => {
    setTrainingFormSessionId(null);
    setShowTrainingFormNew(true);
  }, []);

  // Open the training log form pre-linked to a specific unlinked session so the
  // athlete can create a new training log for it (coach notes included).
  const handleCreateLogForSession = useCallback(
    (session: Session & { id: string }) => {
      if (queryUid) return;
      setTrainingFormSessionId(session.id);
      setShowTrainingFormNew(true);
    },
    [queryUid],
  );

  // Event being logged from "Events to log" (Performance flow seeded from it).
  const [performanceFlowEvent, setPerformanceFlowEvent] =
    useState<PeakEvent | null>(null);
  const [performancesRefreshKey, setPerformancesRefreshKey] = useState(0);

  // Link an unlinked completed session to an existing training log (both sides
  // of the bidirectional link), then refresh the day so it drops off the list.
  const handleLinkSessionToLog = useCallback(
    async (sessionId: string, trainingLogId: string) => {
      const uid = currentUser?.uid;
      if (!uid || queryUid) return;
      setLinkingSessionId(sessionId);
      try {
        await sessionService.linkSessionToLog(
          uid,
          sessionId,
          trainingLogId,
          generateDateID(currentDate),
        );
        // Optimistically drop it from the direct-fetch fallback so it leaves
        // the "Sessions to log" list immediately, even if it wasn't in the
        // cached DayPost.
        setCompletedSessions((prev) => prev.filter((s) => s.id !== sessionId));
        invalidateStreakCache();
        if (isDateBeyondCachedRange) {
          await fetchOnDemandData(currentDate);
        } else {
          await fetchDayPostsData(false);
        }
        await refreshSessionsForActiveDate();
      } catch (err) {
        console.error("Error linking session to training log:", err);
      } finally {
        setLinkingSessionId(null);
      }
    },
    [
      currentUser?.uid,
      queryUid,
      invalidateStreakCache,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      fetchDayPostsData,
      refreshSessionsForActiveDate,
      currentDate,
    ],
  );

  // Refresh both session sources after a session is deleted, rejected or
  // moved: the cached DayPost and the direct-fetch fallback.
  const refreshAfterSessionChange = useCallback(async () => {
    invalidateStreakCache();
    if (isDateBeyondCachedRange) {
      await fetchOnDemandData(currentDate);
    } else {
      await fetchDayPostsData(false);
    }
    await refreshSessionsForActiveDate();
  }, [
    invalidateStreakCache,
    isDateBeyondCachedRange,
    fetchOnDemandData,
    fetchDayPostsData,
    refreshSessionsForActiveDate,
    currentDate,
  ]);

  // Delete the athlete's own session from the bottom sheet. The service
  // unlinks it from any training log and removes it from the day post.
  const handleDeleteSessionFromSheet = useCallback(
    async (session: Session & { id: string }) => {
      const uid = currentUser?.uid;
      if (!uid || queryUid) return;
      await sessionService.deleteSession(uid, session.id);
      setCompletedSessions((prev) => prev.filter((s) => s.id !== session.id));
      setScheduledSessions((prev) => prev.filter((s) => s.id !== session.id));
      await refreshAfterSessionChange();
    },
    [currentUser?.uid, queryUid, refreshAfterSessionChange],
  );

  // Decline a coach-issued session with a reason (the coach is notified).
  const handleRejectSessionFromSheet = useCallback(
    async (session: Session & { id: string }, reason: string) => {
      const uid = currentUser?.uid;
      if (!uid || queryUid) return;
      await sessionService.rejectProposedSession(uid, session.id, reason);
      setCompletedSessions((prev) => prev.filter((s) => s.id !== session.id));
      setScheduledSessions((prev) => prev.filter((s) => s.id !== session.id));
      setProposedSessions((prev) => prev.filter((s) => s.id !== session.id));
      await refreshAfterSessionChange();
    },
    [currentUser?.uid, queryUid, refreshAfterSessionChange],
  );

  // "Link to training log" from the sheet: hand over to the link modal.
  const handleLinkFromSheet = useCallback(
    (session: Session & { id: string }) => {
      setSheetSession(null);
      setLinkModalSession(session);
    },
    [],
  );

  // "Change date" from the sheet or a scheduled card (plan 3c).
  const handleChangeDateRequest = useCallback(
    (session: Session & { id: string }) => {
      if (queryUid) return;
      setSheetSession(null);
      setDateChangeSession(session);
    },
    [queryUid],
  );

  const handleSessionDateChanged = useCallback(async () => {
    setDateChangeSession(null);
    await refreshAfterSessionChange();
  }, [refreshAfterSessionChange]);

  // Handle recovery log edit
  const handleEditRecoveryLog = useCallback(
    (logId: string, log: RecoveryLog & { id: string }) => {
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      const dateID = generateDateID(currentDate);
      setEditingRecoveryLog({ id: logId, data: log, dateID });
      setShowRecoveryForm(true);
    },
    [queryUid, currentDate],
  );

  // Handle recovery log delete
  const handleDeleteRecoveryLog = useCallback(
    async (logId: string) => {
      // Prevent deleting when viewing another user's dashboard
      if (queryUid || !currentUser?.uid) return;

      try {
        const dateID = generateDateID(currentDate);
        await deleteRecoveryLogById(logId, dateID);

        // Refresh the data (cached or on-demand)
        if (isDateBeyondCachedRange) {
          await fetchOnDemandData(currentDate);
        } else {
          await fetchDayPostsData(false);
        }
      } catch (error) {
        console.error("Error deleting recovery log:", error);
      }
    },
    [
      queryUid,
      currentUser?.uid,
      currentDate,
      deleteRecoveryLogById,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      fetchDayPostsData,
    ],
  );

  const handleRecoverySubmit = useCallback(
    async (recoveryLog: any) => {
      if (!currentUser?.uid) return;
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      try {
        const { logDate, ...recoveryData } = recoveryLog || {};
        const dateID = logDate || generateDateID(currentDate);
        let recoveryLogId: string;

        if (editingRecoveryLog) {
          // Update existing recovery log
          await updateRecoveryLogById(editingRecoveryLog.id, recoveryData);
          recoveryLogId = editingRecoveryLog.id;
          // console.log("Recovery log updated successfully:", recoveryLogId);
        } else {
          // Add new recovery log
          recoveryLogId = await addRecoveryLog(
            currentUser.uid,
            recoveryData,
            dateID,
          );

          // Add the recovery log ID to the day post
          await addLogIDToDayPost(
            currentUser.uid,
            dateID,
            recoveryLogId,
            "recoveryLogIDs",
          );

          console.log("Recovery log added successfully:", recoveryLogId);
          if (recoveryToLog && !isScheduledRecoveryLogged(recoveryToLog)) {
            await linkScheduledRecovery(
              currentUser.uid,
              recoveryToLog.id,
              recoveryLogId,
            );
          }
        }

        // Close the form and clear editing state
        setShowRecoveryForm(false);
        setEditingRecoveryLog(null);
        setRecoveryToLog(null);

        // Invalidate streak cache so StreakCard reflects this new log immediately
        invalidateStreakCache();

        // Refresh the data (cached or on-demand)
        if (isDateBeyondCachedRange) {
          await fetchOnDemandData(currentDate);
        } else {
          await fetchDayPostsData(false);
        }
      } catch (error) {
        console.error("Error submitting recovery log:", error);
      }
    },
    [
      currentUser?.uid,
      queryUid,
      currentDate,
      editingRecoveryLog,
      recoveryToLog,
      updateRecoveryLogById,
      fetchDayPostsData,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      invalidateStreakCache,
    ],
  );
  const handleNegativeInfluencesSubmit = useCallback(
  async (negativeInfluencesLog: any) => {
    if (!currentUser?.uid) return;
    if (queryUid) return;

    try {
      const { logDate, ...logData } = negativeInfluencesLog || {};
      const dateID = logDate || generateDateID(currentDate);

      const logId = await addNegativeInfluenceLog(
        currentUser.uid,
        logData,
        dateID,
      );

      await addLogIDToDayPost(
        currentUser.uid,
        dateID,
        logId,
        "negativeInfluenceLogIDs",
      );

      setShowNegativeInfluencesForm(false);
      invalidateStreakCache();

      if (isDateBeyondCachedRange) {
        await fetchOnDemandData(currentDate);
      } else {
        await fetchDayPostsData(false);
      }
    } catch (error) {
      console.error("Error submitting negative influences log:", error);
    }
  },
  [
    currentUser?.uid,
    queryUid,
    currentDate,
    fetchDayPostsData,
    isDateBeyondCachedRange,
    fetchOnDemandData,
    invalidateStreakCache,
  ],
);

  const handleNiggleSubmit = useCallback(
    async (niggleLog: any) => {
      if (!currentUser?.uid) return;
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      try {
        const { logDate, ...niggleData } = niggleLog || {};
        const dateID = logDate || generateDateID(currentDate);

        // Add niggle log
        const niggleLogId = await addNiggleLog(
          currentUser.uid,
          niggleData,
          dateID,
        );

        // Add the niggle log ID to the day post
        await addLogIDToDayPost(
          currentUser.uid,
          dateID,
          niggleLogId,
          "niggleLogIDs",
        );

        // console.log("Niggle log added successfully:", niggleLogId);

        // Close the form
        setShowNiggleForm(false);

        // Invalidate streak cache so StreakCard reflects this new log immediately
        invalidateStreakCache();

        // Refresh the data (cached or on-demand)
        if (isDateBeyondCachedRange) {
          await fetchOnDemandData(currentDate);
        } else {
          await fetchDayPostsData(false);
        }
      } catch (error) {
        console.error("Error adding niggle log:", error);
      }
    },
    [
      currentUser?.uid,
      currentDate,
      fetchDayPostsData,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      invalidateStreakCache,
    ],
  );

  const handleNoteSubmit = useCallback(
    async (notes: string, logDate?: string) => {
      if (!currentUser?.uid) return;
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      try {
        const dateID = logDate || generateDateID(currentDate);

        // Use the hook's submitNoteLog function for proper offline support
        await submitNoteLog(dateID, notes);

        // console.log("Note log added successfully");

        // Close the form
        setShowNoteForm(false);

        // Invalidate streak cache so StreakCard reflects this new log immediately
        invalidateStreakCache();

        // Data refresh is handled by the hook's onSuccess callback
      } catch (error) {
        console.error("Error adding note log:", error);
      }
    },
    [
      currentUser?.uid,
      queryUid,
      currentDate,
      submitNoteLog,
      invalidateStreakCache,
    ],
  );

  const handleHealthSubmit = useCallback(
    async (healthLog: any) => {
      if (!currentUser?.uid) return;
      // Prevent editing when viewing another user's dashboard
      if (queryUid) return;

      const { logDate, ...healthData } = healthLog || {};
      const dateID = logDate || generateDateID(currentDate);

      try {
        const healthLogId = await addHealthLog(
          currentUser.uid,
          healthData,
          dateID,
        );

        await addLogIDToDayPost(
          currentUser.uid,
          dateID,
          healthLogId,
          "healthLogIDs",
        );
      } catch (error) {
        console.error("Error adding health log:", error);
        throw error;
      }

      setShowHealthForm(false);
      invalidateStreakCache();

      const beyondCachedRange = isDateBeyondCachedRange;
      const targetDate = currentDate;
      const refresh = async () => {
        if (beyondCachedRange) {
          await fetchOnDemandData(targetDate);
        } else {
          await fetchDayPostsData(false);
        }
      };

      try {
        await refresh();
      } catch (refreshError) {
        console.error("Error refreshing after health log:", refreshError);
      }

      void (async () => {
        try {
          const peakScoreResult = await calculateAndSavePeakScore(dateID);
          if (!peakScoreResult.success) {
            console.warn(
              "Peak score calculation failed (background):",
              peakScoreResult.error,
            );
          }
          await refresh();
        } catch (bgErr) {
          console.error("Background peak score calculation failed:", bgErr);
        }
      })();
    },
    [
      currentUser?.uid,
      queryUid,
      currentDate,
      fetchDayPostsData,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      invalidateStreakCache,
    ],
  );

  // Handle activity linking
  const handleOpenActivityLinkModal = useCallback(
    (activity: ActivityLog & { id: string }) => {
      setSelectedActivity(activity);
      setShowActivityLinkModal(true);
    },
    [],
  );

  const handleCloseActivityLinkModal = useCallback(() => {
    setShowActivityLinkModal(false);
    setSelectedActivity(null);
  }, []);

  const handleActivityLinkSuccess = useCallback(async () => {
    // Refresh the data (cached or on-demand)
    if (isDateBeyondCachedRange) {
      await fetchOnDemandData(currentDate);
    } else {
      await fetchDayPostsData(false);
    }
  }, [
    fetchDayPostsData,
    isDateBeyondCachedRange,
    fetchOnDemandData,
    currentDate,
  ]);

  const getActivityDedupKey = useCallback(
    (activity: ActivityLog & { id: string }): string => {
      return [
        activity.terraActivityDataID || "",
        activity.provider || "",
        activity.startTime || "",
        activity.endTime || "",
        String(activity.duration ?? ""),
        (activity.sessionName || "").trim().toLowerCase(),
        String(activity.calories ?? ""),
        String(activity.workKilojoules ?? ""),
      ].join("|");
    },
    [],
  );

  // Handle ignoring an activity so it is hidden from UI
  const handleIgnoreActivity = useCallback(
    async (activity: ActivityLog & { id: string }) => {
      const userId = currentUser?.uid;
      if (!userId) return;
      const selectedKey = getActivityDedupKey(activity);
      setIgnoringActivityKeys((prev) => {
        const next = new Set(prev);
        next.add(selectedKey);
        return next;
      });

      try {
        const allActivities = activeDayPost?.activityLogs || [];
        const duplicateIds = allActivities
          .filter(
            (candidate) =>
              !candidate.linkedTrainingLogID &&
              !candidate.ignore &&
              getActivityDedupKey(candidate) === selectedKey,
          )
          .map((candidate) => candidate.id);

        const idsToIgnore =
          duplicateIds.length > 0 ? duplicateIds : [activity.id];

        await Promise.all(
          idsToIgnore.map((activityId) =>
            updateActivityLog(userId, activityId, { ignore: true }),
          ),
        );

        if (isDateBeyondCachedRange) {
          await fetchOnDemandData(currentDate);
        } else {
          await fetchDayPostsData(false);
        }
      } catch (error) {
        console.error("Error ignoring activity:", error);
      } finally {
        setIgnoringActivityKeys((prev) => {
          const next = new Set(prev);
          next.delete(selectedKey);
          return next;
        });
      }
    },
    [
      currentUser?.uid,
      activeDayPost,
      isDateBeyondCachedRange,
      fetchOnDemandData,
      currentDate,
      fetchDayPostsData,
      getActivityDedupKey,
    ],
  );

  // Effect to fetch on-demand data when date is beyond cached range
  useEffect(() => {
    if (isDateBeyondCachedRange) {
      fetchOnDemandData(currentDate);
    } else {
      // Clear on-demand data when we're back in cached range
      setOnDemandData(null);
      setOnDemandError(null);
      setOnDemandLoading(false);
    }
  }, [isDateBeyondCachedRange, currentDate, fetchOnDemandData]);

  // Helper functions for filtering linked/unlinked data
  const getUnlinkedActivities = useCallback(
    (
      activities: (ActivityLog & { id: string })[],
    ): (ActivityLog & { id: string })[] => {
      return activities.filter(
        (activity) => !activity.linkedTrainingLogID && !activity.ignore,
      );
    },
    [],
  );

  const getUnlinkedTrainingLogs = useCallback(
    (
      trainingLogs: (TrainingLog & { id: string })[],
    ): (TrainingLog & { id: string })[] => {
      return trainingLogs.filter((training) => !training.linkedActivityLogID);
    },
    [],
  );

  const getLinkedActivityForTraining = useCallback(
    (
      trainingLog: TrainingLog & { id: string },
      activities: (ActivityLog & { id: string })[],
    ): (ActivityLog & { id: string }) | null => {
      if (!trainingLog.linkedActivityLogID) return null;
      return (
        activities.find(
          (activity) => activity.id === trainingLog.linkedActivityLogID,
        ) || null
      );
    },
    [],
  );

  // Format date for display
  const formatDisplayDate = useCallback((date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  // Check if the current date is today
  const isToday = useCallback((date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  // Pull-to-refresh touch handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (window.scrollY === 0 && isToday(currentDate) && !isRefreshing) {
        touchStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    },
    [currentDate, isRefreshing, isToday],
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0 && window.scrollY === 0) {
      setPullDistance(Math.min(diff * 0.5, 120));
    } else {
      isPulling.current = false;
      setPullDistance(0);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance >= PULL_THRESHOLD) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);
      setTimeout(() => window.location.reload(), 300);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance]);

  // Check if the current date is in the future
  const isFuture = useCallback((date: Date): boolean => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return date.getTime() > today.getTime();
  }, []);

  // Calculate wellness logging frequency for the past week
  const calculateWellnessLoggingFrequency = useCallback((): {
    loggedDays: number;
    totalDays: number;
  } => {
    if (!dayPosts || dayPosts.length === 0)
      return { loggedDays: 0, totalDays: 7 };

    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6); // Past 7 days including today

    let loggedDays = 0;

    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(sevenDaysAgo);
      checkDate.setDate(sevenDaysAgo.getDate() + i);
      const dateID = generateDateID(checkDate);

      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      if (dayPost && dayPost.wellnessLogs && dayPost.wellnessLogs.length > 0) {
        loggedDays++;
      }
    }

    return { loggedDays, totalDays: 7 };
  }, [dayPosts]);

  // Check if user has sufficient wellness logs for peak score calculation
  const hasSufficientWellnessLogs = useMemo(() => {
    const { loggedDays } = calculateWellnessLoggingFrequency();
    return loggedDays >= 4;
  }, [calculateWellnessLoggingFrequency]);

  // Daily insight logic removed - handled by AIInsightCard component

  // Calculate total load from training logs
  const calculateTotalLoad = useCallback((trainingLogs: any[]): number => {
    if (trainingLogs.length === 0) return 0;
    return trainingLogs.reduce((total, log) => total + (log.load || 0), 0);
  }, []);

  // Calculate total recovery points from recovery logs
  const calculateTotalRecoveryPoints = useCallback(
  (recoveryLogs: any[]): number => {
    if (recoveryLogs.length === 0) return 0;
    const total = recoveryLogs.reduce(
      (sum, log) => sum + (log.pointsAmount || 0),
      0,
    );
    return Math.min(total, 15);
  },
  [],
);

const calculateTotalNegativeInfluencePoints = useCallback(
  (negativeInfluencesLogs: any[]): number => {
    if (negativeInfluencesLogs.length === 0) return 0;
    const total = negativeInfluencesLogs.reduce(
      (sum, log) => sum + (log.pointsAmount || 0),
      0,
    );
    return Math.min(total, 25);
  },
  [],
);

  // Select most recent health log
  // const latestHealthLog = useMemo(() => {
  //   if (
  //     !activeDayPost ||
  //     !activeDayPost.healthLogs ||
  //     activeDayPost.healthLogs.length === 0
  //   ) {
  //     return null;
  //   }
  //   // Get the most recent health log (last in array) - consistent with SleepInsights
  //   return activeDayPost.healthLogs[activeDayPost.healthLogs.length - 1];
  // }, [activeDayPost?.healthLogs]);
  //Now, will select most highest sleep duration
  const latestHealthLog = useMemo(() => {
    if (
      !activeDayPost ||
      !activeDayPost.healthLogs ||
      activeDayPost.healthLogs.length === 0
    ) {
      return null;
    }
    // Pick the health log with the longest sleep duration, not just the last one.
    return activeDayPost.healthLogs.reduce((max, log) =>
      (log.sleepDuration || 0) > (max.sleepDuration || 0) ? log : max
    );
  }, [activeDayPost?.healthLogs]);

  // Calculate derived values
  const wellnessScore = activeDayPost
    ? calculateWellnessScore(activeDayPost.wellnessLogs)
    : 0;
  const peakScore = activeDayPost
    ? Math.round(calculatePeakScore(activeDayPost.peakScoreLog))
    : 0;
  const sleepDuration = activeDayPost
    ? calculateSleepDuration(
        activeDayPost.healthLogs,
        activeDayPost.wellnessLogs,
      )
    : 0;
  const totalLoad = activeDayPost
    ? calculateTotalLoad(activeDayPost.trainingLogs)
    : 0;
  const totalNegativeInfluencePoints = activeDayPost
  ? calculateTotalNegativeInfluencePoints(
      activeDayPost.negativeInfluencesLogs || [],
    )
  : 0;
  const totalRecoveryPoints = activeDayPost
    ? calculateTotalRecoveryPoints(activeDayPost.recoveryLogs)
    : 0;
  const netRecoveryDisplay = Math.max(
  totalRecoveryPoints - totalNegativeInfluencePoints,
  0,
);  
  // Calculate HLR score and health metrics percentage differences
  const { hlrScore, healthMetricsComparison } = useMemo(() => {
    const currentDayHLR = activeDayPost?.peakScoreLog?.hlr ?? null;

    // Get last 7 days of health data for comparison
    const last7Days = dayPosts
      .slice(-7)
      .filter((post) => post.healthLogs && post.healthLogs.length > 0);

    if (last7Days.length < 3 || !latestHealthLog) {
      return {
        hlrScore: currentDayHLR,
        healthMetricsComparison: {
          rhrPercentDiff: 0,
          hrvPercentDiff: 0,
          respPercentDiff: 0,
        },
      };
    }

    // Calculate 7-day averages
    const rhr7DayAvg =
      last7Days.reduce((sum, post) => {
        const latestLog = post.healthLogs[post.healthLogs.length - 1];
        return sum + (latestLog?.rhr || 0);
      }, 0) / last7Days.length;

    const hrv7DayAvg =
      last7Days.reduce((sum, post) => {
        const latestLog = post.healthLogs[post.healthLogs.length - 1];
        return sum + (latestLog?.hrv || 0);
      }, 0) / last7Days.length;

    const resp7DayAvg =
      last7Days.reduce((sum, post) => {
        const latestLog = post.healthLogs[post.healthLogs.length - 1];
        return sum + (latestLog?.respRate || 0);
      }, 0) / last7Days.length;

    // Calculate percentage differences
    const calculatePercentDiff = (
      today: number,
      avg: number,
      lowerIsBetter: boolean = false,
    ): number => {
      if (today === 0 || avg === 0) return 0;
      const rawDiff = ((today - avg) / avg) * 100;
      return lowerIsBetter ? -rawDiff : rawDiff;
    };

    const rhrPercentDiff = latestHealthLog.rhr
      ? calculatePercentDiff(latestHealthLog.rhr, rhr7DayAvg, true)
      : 0;
    const hrvPercentDiff = latestHealthLog.hrv
      ? calculatePercentDiff(latestHealthLog.hrv, hrv7DayAvg, false)
      : 0;
    const respPercentDiff = latestHealthLog.respRate
      ? calculatePercentDiff(latestHealthLog.respRate, resp7DayAvg, true)
      : 0;

    return {
      hlrScore: currentDayHLR,
      healthMetricsComparison: {
        rhrPercentDiff,
        hrvPercentDiff,
        respPercentDiff,
      },
    };
  }, [activeDayPost, dayPosts, latestHealthLog]);

  // Wearable daily (per-device) docs
  const [terraDailyDocs, setTerraDailyDocs] = useState<TerraDailyDoc[]>([]);
  const [terraDailyLoading, setTerraDailyLoading] = useState<boolean>(false);

  useEffect(() => {
    // console.log("[Dashboard] Daily data fetch effect triggered");
    // console.log("[Dashboard] Current date:", currentDate);
    // console.log("[Dashboard] Query UID:", queryUid);
    // console.log("[Dashboard] Current user:", currentUser?.uid);

    (async () => {
      try {
        const effectiveUserId = queryUid || currentUser?.uid;

        if (!effectiveUserId) {
          // console.log(
          //   "[Dashboard] No user ID available, skipping daily data fetch",
          // );
          setTerraDailyDocs([]);
          return;
        }

        // Convert current date to YYYY-MM-DD format
        // const dateStr = currentDate.toISOString().split("T")[0];
        const dateStr = currentDate.toLocaleDateString("en-CA");
        // console.log(
        //   "[Dashboard] Fetching daily data for date:",
        //   dateStr,
        //   "user:",
        //   effectiveUserId,
        // );

        setTerraDailyLoading(true);

        try {
          // Try the new cloud function method first (GCS bucket)
          // console.log(
          //   "[Dashboard] Attempting to fetch from GCS bucket via cloud function",
          //   dateStr,
          // );
          const functions = getFunctions();
          const getDailyData = httpsCallable(functions, "getDailyData");

          // console.log("[Dashboard] Calling getDailyData with date:", dateStr);
          const result = await getDailyData({
            date: dateStr,
            userId: queryUid || undefined, // Only pass if viewing another user
          });
          // console.log("[Dashboard] Cloud function response received:", result);

          const response = result.data as {
            success: boolean;
            data: any[];
            count: number;
            date: string;
          };
          // console.log(
          //   "[Dashboard] Parsed response - success:",
          //   response.success,
          //   "data count:",
          //   response.data?.length,
          // );

          if (response.success && response.data && response.data.length > 0) {
            // console.log(
            //   "[Dashboard] ✅ Successfully fetched daily data from GCS bucket via cloud function:",
            //   response.data.length,
            //   "docs",
            // );
            setTerraDailyDocs(response.data as TerraDailyDoc[]);
            return; // Success, no need for fallback
          } else {
            // console.log(
            //   "[Dashboard] No daily data returned from cloud function, falling back to Firestore",
            // );
          }
        } catch (cloudFunctionError) {
          // console.log(
          //   "[Dashboard] Cloud function failed, falling back to Firestore:",
          //   cloudFunctionError,
          // );
        }

        // Fallback to old Firestore method (if wearableDayData exists)
        const refs = dayPostData?.wearableDayData || [];
        if (refs.length > 0) {
          // console.log(
          //   "[Dashboard] Using fallback: fetching daily data from Firestore with",
          //   refs.length,
          //   "refs",
          // );
          const docs = await terraDailyService.getDailyForDay(refs);
          // console.log(
          //   "[Dashboard] Firestore fallback returned",
          //   docs.length,
          //   "docs",
          // );
          setTerraDailyDocs(docs);
        } else {
          // console.log(
          //   "[Dashboard] No fallback data available (no wearableDayData refs)",
          // );
          setTerraDailyDocs([]);
        }
      } catch (err) {
        // console.error(
        //   "[Dashboard] ❌ Failed to fetch Terra daily docs (both methods):",
        //   err,
        // );
        setTerraDailyDocs([]);
      } finally {
        // console.log(
        //   "[Dashboard] Daily data fetch complete, loading set to false",
        // );
        setTerraDailyLoading(false);
      }
    })();
  }, [currentDate, currentUser?.uid, queryUid, dayPostData?.wearableDayData]);
  useEffect(() => {
    const walkthroughEnabled = searchParams.get("walkthrough") === "true";

    if (walkthroughEnabled) {
      const timer = setTimeout(() => {
        startTour?.();
        // Optional: remove query param from URL after starting the tour
        setSearchParams({});
      }, 1000); // delay 1 second for full content render

      return () => clearTimeout(timer);
    }
  }, [searchParams, startTour, setSearchParams]);

  // Fetch cycle tracking data - only for female users
  useEffect(() => {
    const fetchCycleData = async () => {
      const effectiveUserId = queryUid || currentUser?.uid;
      if (!effectiveUserId) return;

      // Only fetch cycle data for female users
      if (userData?.gender?.toLowerCase() !== "female") {
        setCycleStats(null);
        setCycleLoading(false);
        return;
      }

      try {
        setCycleLoading(true);
        const logs = await getCycleLogs(effectiveUserId);
        const stats = calculateCycleStats(logs);
        setCycleStats(stats);
      } catch (error) {
        console.error("Error loading cycle data:", error);
        setCycleStats(null);
      } finally {
        setCycleLoading(false);
      }
    };

    fetchCycleData();
  }, [currentUser?.uid, queryUid, userData?.gender]);

  // Build per-device derived displays (no cross-device aggregation)
  const perDeviceDisplays = useMemo(() => {
    return terraDailyDocs.map((doc: any) => {
      // Totals
      const stepsTotal = (doc?.steps_data?.steps ||
        doc?.distance_data?.steps ||
        0) as number;
      const distanceTotal = (doc?.distance_data?.distance_meters ||
        0) as number;
      const caloriesTotal = (doc?.calories_data?.total_burned_calories ||
        0) as number;
      const oxygenPct =
        typeof (doc as any)?.oxygen_data?.avg_spo2_percent === "number"
          ? (doc as any).oxygen_data.avg_spo2_percent
          : 0;

      // Steps histogram per hour
      const samplesSteps: Array<{ timestamp: string; steps?: number }> =
        doc?.steps_data?.step_samples ||
        doc?.distance_data?.detailed?.step_samples ||
        doc?.distance_data?.step_samples ||
        [];
      const hourlyMap: Record<string, number> = {};
      if (Array.isArray(samplesSteps) && samplesSteps.length > 0) {
        const sorted = [...samplesSteps]
          .map((s: any) => ({
            t: new Date(s.timestamp),
            v: Number(s.steps) || 0,
          }))
          .sort((a, b) => a.t.getTime() - b.t.getTime());
        const isNonDecreasing = sorted.every(
          (p, i) => i === 0 || p.v >= sorted[i - 1].v,
        );
        let prev: number | null = null;
        sorted.forEach(({ t, v }) => {
          let inc = 0;
          if (isNonDecreasing) {
            if (prev !== null) {
              inc = v - prev;
              if (!Number.isFinite(inc) || inc < 0) inc = 0;
            }
            prev = v;
          } else {
            inc = Number.isFinite(v) && v > 0 ? v : 0;
          }
          const bucket = new Date(t);
          const hourKey = new Date(bucket.setMinutes(0, 0, 0)).toISOString();
          hourlyMap[hourKey] = (hourlyMap[hourKey] || 0) + inc;
        });
      }
      const base = new Date(currentDate);
      base.setHours(0, 0, 0, 0);
      const hours: { label: string; value: number }[] = [];
      for (let h = 0; h < 24; h++) {
        const d = new Date(base.getTime() + h * 60 * 60 * 1000);
        const key = new Date(d.setMinutes(0, 0, 0)).toISOString();
        const value = hourlyMap[key] || 0;
        hours.push({ label: `${("" + h).padStart(2, "0")}`, value });
      }
      const stepsMax = Math.max(0, ...hours.map((h) => h.value));

      // Distance sparkline (per minute)
      const distSamples =
        doc?.distance_data?.detailed?.distance_samples ||
        doc?.distance_data?.distance_samples ||
        [];
      const distMinuteToValue = new Map<number, number>();
      if (Array.isArray(distSamples)) {
        distSamples.forEach((s: any) => {
          const t = new Date(s.timestamp).getTime();
          const key = Math.floor(t / 60000);
          const v = Number(s.distance_meters) || 0;
          distMinuteToValue.set(key, (distMinuteToValue.get(key) || 0) + v);
        });
      }
      const distValuesAll = Array.from(distMinuteToValue.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v);
      const distMaxPts = 60;
      const distValues =
        distValuesAll.length > distMaxPts
          ? distValuesAll.filter(
              (_, i) => i % Math.ceil(distValuesAll.length / distMaxPts) === 0,
            )
          : distValuesAll;

      // Calories sparkline (per minute)
      const calArr = Array.isArray(doc?.calories_data)
        ? (doc as any).calories_data
        : doc?.calories_data?.calorie_samples || [];
      const calMinuteToValue = new Map<number, number>();
      if (Array.isArray(calArr)) {
        calArr.forEach((s: any) => {
          const t = new Date(s.timestamp).getTime();
          const key = Math.floor(t / 60000);
          const v = Number(s.calories) || 0;
          calMinuteToValue.set(key, (calMinuteToValue.get(key) || 0) + v);
        });
      }
      const calValuesAll = Array.from(calMinuteToValue.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v);
      const calMaxPts = 60;
      const calValues =
        calValuesAll.length > calMaxPts
          ? calValuesAll.filter(
              (_, i) => i % Math.ceil(calValuesAll.length / calMaxPts) === 0,
            )
          : calValuesAll;

      return {
        provider: (doc?.provider || "Device") as string,
        totals: {
          steps: stepsTotal,
          distance: distanceTotal,
          calories: caloriesTotal,
          oxygen: oxygenPct,
        },
        stepsHistogram: { hours, max: stepsMax },
        distanceSparkValues: distValues,
        caloriesSparkValues: calValues,
      };
    });
  }, [terraDailyDocs, activeDate]);

  // Check if user has connected wearable devices
  const hasConnectedWearableDevices = useMemo(() => {
    if (!userData?.deviceIDs) return false;
    const activeDevices = (userData.deviceIDs as any[]).filter(
      (device: any) => !device.expired,
    );
    return activeDevices.length > 0;
  }, [userData?.deviceIDs]);

  // Pick winners by metric
  const winnerSteps = useMemo(() => {
    let best = null as (typeof perDeviceDisplays)[number] | null;
    perDeviceDisplays.forEach((d) => {
      if (!best || (d.totals.steps || 0) > (best.totals.steps || 0)) best = d;
    });
    return best && (best.totals.steps || 0) > 0 ? best : null;
  }, [perDeviceDisplays]);

  const winnerDistance = useMemo(() => {
    let best = null as (typeof perDeviceDisplays)[number] | null;
    perDeviceDisplays.forEach((d) => {
      if (!best || (d.totals.distance || 0) > (best.totals.distance || 0))
        best = d;
    });
    return best && (best.totals.distance || 0) > 0 ? best : null;
  }, [perDeviceDisplays]);

  const winnerCalories = useMemo(() => {
    let best = null as (typeof perDeviceDisplays)[number] | null;
    perDeviceDisplays.forEach((d) => {
      if (!best || (d.totals.calories || 0) > (best.totals.calories || 0))
        best = d;
    });
    return best && (best.totals.calories || 0) > 0 ? best : null;
  }, [perDeviceDisplays]);

  const winnerOxygen = useMemo(() => {
    let best = null as (typeof perDeviceDisplays)[number] | null;
    perDeviceDisplays.forEach((d) => {
      if (!best || (d.totals.oxygen || 0) > (best.totals.oxygen || 0)) best = d;
    });
    return best && (best.totals.oxygen || 0) > 0 ? best : null;
  }, [perDeviceDisplays]);

  // Get filtered data for linking
  const unlinkedActivities = useMemo(() => {
    if (!activeDayPost) return [];

    const allActivities = activeDayPost.activityLogs;

    // Track dedup keys of activities that are already linked to a training log so
    // we can hide their unlinked duplicates from the "Link Activities" list.
    // Without this, after linking one activity, any duplicate activity (same
    // wearable activity reported via a different source) would resurface here.
    const linkedDedupKeys = new Set<string>();
    allActivities.forEach((activity) => {
      if (activity.linkedTrainingLogID) {
        linkedDedupKeys.add(getActivityDedupKey(activity));
      }
    });

    const sourceActivities = getUnlinkedActivities(allActivities);
    const seenKeys = new Set<string>();

    return sourceActivities.filter((activity) => {
      const dedupKey = getActivityDedupKey(activity);
      if (linkedDedupKeys.has(dedupKey)) return false;
      if (seenKeys.has(dedupKey)) return false;
      seenKeys.add(dedupKey);
      return true;
    });
  }, [activeDayPost, getUnlinkedActivities, getActivityDedupKey]);

  const unlinkedTrainingLogs = activeDayPost
    ? getUnlinkedTrainingLogs(activeDayPost.trainingLogs)
    : [];

  // Convert values to percentages for display
  const peakPercentage = Math.min((peakScore / 110) * 100, 100); // Peak out of 110
  const sleepDurationHours = sleepDuration / 3600;
  const sleepGoalHours = userData?.sleepGoal ?? 8;
  const loadGoalTarget = userData?.loadGoal ?? 400;
  const weeklyLoadGoalTarget = (userData as any)?.weeklyLoadGoal ?? 2800;

  // Calculate current week's total load and daily loads (Monday-Sunday)
  const { currentWeekLoad, weeklyDailyLoads } = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days to Monday

    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    let weekTotal = 0;
    const dailyLoads: number[] = [];

    // Get loads for Monday through Sunday (7 days)
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const dateID = generateDateID(date);

      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      const dayLoad = (dayPost?.trainingLogs || []).reduce(
        (sum, log) => sum + (log.load || 0),
        0,
      );
      dailyLoads.push(dayLoad);
      weekTotal += dayLoad;
    }

    return { currentWeekLoad: weekTotal, weeklyDailyLoads: dailyLoads };
  }, [dayPosts]);

  const sleepPercentage = Math.min(
    (sleepDurationHours / sleepGoalHours) * 100,
    100,
  );
  const totalLoadPercentage = Math.min((totalLoad / loadGoalTarget) * 100, 100);

  return (
    <div
      className="w-full pt-16 pb-28"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Header />

      <DashboardBackground>
        {/* Pull-to-refresh indicator — inside DashboardBackground so the hero image extends behind it */}
        {pullDistance > 0 && (
          <div
            className="flex items-center justify-center transition-all duration-150 overflow-hidden"
            style={{ height: pullDistance }}
          >
            <ArrowPathIcon
              className={`h-5 w-5 text-white/70 transition-transform duration-200 ${
                isRefreshing ? "animate-spin" : ""
              }`}
              style={{
                transform: `rotate(${Math.min((pullDistance / PULL_THRESHOLD) * 360, 360)}deg)`,
                opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
              }}
            />
          </div>
        )}

        {/* Cache/Offline Status Indicators - similar to Feed page */}
        {isOffline && (
          <div className="bg-orange-500/20 p-3 mb-4">
            <div className="flex items-center space-x-2 text-orange-700">
              <BellIcon className="h-5 w-5" />
              <span className="text-sm font-medium">
                You're currently offline. Showing cached data.
              </span>
            </div>
          </div>
        )}

        {/* Admin viewing indicator */}
        {queryUid && viewedUser && (
          <div className="bg-blue-500/10 border-blue-200 border p-3 mb-4">
            <div className="flex items-center space-x-2 text-blue-700">
              <EyeIcon className="h-5 w-5" />
              <span className="text-sm font-medium">
                Viewing {viewedUser.username}'s dashboard
              </span>
            </div>
          </div>
        )}

        {!queryUid && <DashboardBanners />}

        <div className="py-2 pb-0 !bg-transparent">
          <div className="mx-auto max-w-3xl px-2">
            <div>
              <div className="flex items-center justify-between space-x-4 p-4 w-full pb-2 px-2">
                <div className="flex items-center space-x-3">
                  {/* Back Button */}
                  <button
                    onClick={goToPreviousDay}
                    className="p-2 rounded-full hover:bg-base-200 transition-colors"
                    disabled={combinedLoading}
                  >
                    <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
                  </button>

                  {/* Date Display */}
                  <div className="text-xs font-medium text-base-content font-grotesk">
                    {isToday(activeDate)
                      ? "Today"
                      : formatDisplayDate(activeDate)}
                    {isFuture(currentDate) && (
                      <span className="ml-2 text-sm text-orange-600 font-normal">
                        (Future)
                      </span>
                    )}
                  </div>
                  {/* Calendar Icon Button - Open Date Picker */}
                  <div className="relative">
                    <button
                      onClick={handleOpenDatePicker}
                      className="p-2 rounded-full hover:bg-base-200 transition-colors"
                      disabled={combinedLoading}
                      title="Select Date"
                    >
                      <CalendarIcon className="h-5 w-5 text-base-content/80" />
                    </button>

                    {/* Invisible date input overlay - sized to match button exactly */}
                    <input
                      ref={dateInputRef}
                      type="date"
                      // value={activeDate.toISOString().split("T")[0]}
                      value={`${activeDate.getFullYear()}-${String(activeDate.getMonth() + 1).padStart(2, "0")}-${String(activeDate.getDate()).padStart(2, "0")}`}
                      onChange={(e) => handleDateSelect(e.target.value)}
                      className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                      title="Select Date"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  {/* Refresh Button - Only show when viewing today */}
                  {isToday(currentDate) && (
                    <button
                      onClick={() => window.location.reload()}
                      className="p-2 rounded-full hover:bg-base-200 transition-colors"
                      title="Refresh"
                    >
                      <ArrowPathIcon className="h-4 w-4 text-base-content/60 animate-[spin_0.6s_ease-out_1]" />
                    </button>
                  )}

                  {/* 3-dot dropdown menu - Only show when viewing today */}
                  {isToday(currentDate) && (
                    <div className="relative">
                      <button
                        onClick={() => setShowDashboardMenu(!showDashboardMenu)}
                        className="p-2 rounded-full hover:bg-base-200 transition-colors"
                        title="More options"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5 text-base-content/80" />
                      </button>

                      {showDashboardMenu && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowDashboardMenu(false)}
                          />
                          <div className="absolute right-0 top-full mt-1 z-50 bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[180px]">
                            <button
                              onClick={() => {
                                setShowDashboardInfoDialog(true);
                                setShowDashboardMenu(false);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-base-content hover:bg-base-200 transition-colors font-grotesk"
                            >
                              <SparklesIcon className="h-4 w-4 text-base-content/60" />
                              Peak Score Help
                            </button>
                            <button
                              onClick={() => {
                                setShowDashboardExport(true);
                                setShowDashboardMenu(false);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-base-content hover:bg-base-200 transition-colors font-grotesk"
                            >
                              <ShareIcon className="h-4 w-4 text-base-content/60" />
                              Export Overlay
                            </button>
                            {isDailyInsightDismissed && (
                              <button
                                onClick={() => {
                                  handleShowDailyInsight();
                                  setShowDashboardMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-base-content hover:bg-base-200 transition-colors font-grotesk"
                              >
                                <EyeIcon className="h-4 w-4 text-base-content/60" />
                                Show AI Insight
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Forward Button - Hide when viewing today */}
                  {!isToday(currentDate) && (
                    <button
                      onClick={goToNextDay}
                      className="p-2 rounded-full hover:bg-base-200 transition-colors"
                      disabled={combinedLoading}
                    >
                      <ChevronRightIcon className="h-5 w-5 text-base-content/80" />
                    </button>
                  )}
                </div>
              </div>

              {error && (
                <div className="p-4 text-center">
                  <div className="text-red-500 text-sm">{error}</div>
                </div>
              )}

              {/* Main Metrics - Hide for casual users */}
              {!userData?.casualMode && (
                <>
                  <div className="p-4 pb-0">
                    {/* Peak Score with Recovery Points — Plus+ only, hidden for free users or when hidePeakScore is set */}
                    {isPlusOrHigher && !userData?.hidePeakScore && (
                      <div className="mb-6 grid grid-cols-1 items-center md:grid-cols-[1fr_auto_1fr]">
                        <div
                          id="peak-title"
                          className="text-center space-y-3 flex flex-col items-center justify-self-center md:col-start-2"
                        >
                          {!hasSufficientWellnessLogs ? (
                            <PeakScoreProgress
                              daysLogged={
                                calculateWellnessLoggingFrequency().loggedDays
                              }
                              daysRequired={4}
                              size="7rem"
                            />
                          ) : (
                            <>
                              <PeakRecoveryProgress
                                peakScore={peakScore}
                                recoveryPoints={totalRecoveryPoints}
                                negativeInfluencePoints={totalNegativeInfluencePoints}
                                size="7rem"
                                thickness="6px"
                                textSize="text-5xl"
                                disabled={peakScore === 0}
                                onClick={
                                  queryUid || peakScore === 0
                                    ? undefined
                                    : goToPeakInsights
                                }
                                onAddRecovery={
                                  queryUid ? undefined : handleOpenRecoveryForm
                                }
                              />
                              <div
                                className={peakScore === 0 ? "opacity-50" : ""}
                              >
                                <p className="text-center font-grotesk font-semibold text-lg">
                                  Peak
                                </p>
                                <p className="text-xs font-grotesk -mt-1">
                                  Readiness
                                </p>
                              </div>
                              {!combinedLoading && peakScore === 0 && (
                                <p className="text-xs text-base-content/60">
                                  Add wellness log to see your peak score
                                </p>
                              )}
                              {!combinedLoading &&
                              peakScore > 0 &&
                              netRecoveryDisplay > 0 && (
                                <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                                  <span className="font-bold bg-blue-500/10 p-1 rounded-lg">
                                    +{netRecoveryDisplay}
                                  </span>{" "}
                                  recovery
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Other Metrics */}
                    <div
                      className="flex justify-between gap-3 text-sm px-2"
                      id="main-title"
                    >
                      {/* Wellness Score */}
                      {!userData?.hideWellnessSleep && (
                      <div
                        className="text-center space-y-2 p-2 rounded-md flex-1"
                        id="wellness-title"
                      >
                        <button
                          onClick={
                            wellnessScore === 0 && !queryUid
                              ? handleOpenWellnessForm
                              : goToWellnessInsights
                          }
                          className="focus:outline-none hover:opacity-80 transition-opacity"
                          title={
                            wellnessScore === 0
                              ? "Add wellness log"
                              : "View wellness insights"
                          }
                        >
                          <RadialProgress
                            value={wellnessScore === 0 ? 0 : wellnessScore}
                            size="4rem"
                            thickness="3px"
                            color="text-green-500"
                            text={
                              wellnessScore === 0
                                ? "0"
                                : `${Math.round(wellnessScore)}`
                            }
                          />
                        </button>
                        <p className="text-center font-grotesk text-xs font-bold">
                          Wellness
                        </p>
                      </div>
                      )}

                      {/* Total Load */}
                      <div
                        className="text-center space-y-2 p-2 rounded-md flex-1"
                        id="load-title"
                      >
                        <button
                          onClick={goToTrainingInsights}
                          className="focus:outline-none hover:opacity-80 transition-opacity"
                          title="View training insights"
                        >
                          <RadialProgress
                            value={totalLoad === 0 ? 0 : totalLoadPercentage}
                            size="6rem"
                            thickness="5px"
                            color="text-base-content/70"
                            text={
                              totalLoad === 0 ? "0" : `${Math.round(totalLoad)}`
                            }
                            textSize="text-4xl"
                          />
                        </button>
                        <p className="text-center font-grotesk text-xs font-bold">
                          Load
                        </p>
                      </div>

                      {/* Sleep Score */}
                      {!userData?.hideWellnessSleep && (
                      <div
                        className="text-center space-y-2 p-2 rounded-md flex-1"
                        id="sleep-title"
                      >
                        <button
                          onClick={goToSleepInsights}
                          className="focus:outline-none hover:opacity-80 transition-opacity"
                          title="View sleep insights"
                        >
                          <RadialProgress
                            value={Math.round(sleepPercentage)}
                            size="4rem"
                            thickness="3px"
                            color="text-purple-500"
                            text={(() => {
                              let hours = Math.floor(sleepDurationHours);
                              let minutes = Math.round(
                                (sleepDurationHours % 1) * 60,
                              );
                              if (minutes === 60) {
                                hours += 1;
                                minutes = 0;
                              }
                              return hours > 0
                                ? `${hours}h ${minutes}m`
                                : `${minutes}m`;
                            })()}
                            textSize="text-sm"
                          />
                        </button>
                        <p className="text-center font-grotesk text-xs font-bold">
                          Sleep
                        </p>
                      </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-4 mr-4 pb-4">
                    {/* Next event countdown (Events.md §5) */}
                    {!queryUid && isToday(currentDate) && (
                      <NextEventChip userId={currentUser?.uid} />
                    )}
                    {!queryUid && (
                      <>
                        {wellnessScore === 0 && isToday(currentDate) ? (
                          <div className="rounded-lg px-4 py-4 mt-3 bg-green-700 ">
                            <button
                              aria-label="Log wellness"
                              onClick={handleOpenWellnessForm}
                              className="w-full flex justify-between items-center"
                            >
                              <div>
                                <p className="text-lg font-grotesk text-white text-left">
                                  How are you{" "}
                                  <span className="font-bold">feeling?</span>
                                </p>
                                <p className="text-xs text-white font-grotesk text-left">
                                  Complete your{" "}
                                  <strong className="text-green-200">
                                    wellness check-in
                                  </strong>
                                </p>
                              </div>
                              <ArrowRightIcon className="h-10 w-10 bg-green-800 text-white rounded-full p-2" />
                            </button>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Daily Insight - Only show for today and athlete users */}
        {isToday(currentDate) &&
          !userData?.casualMode &&
          !userData?.hidePeakScore && (
          <DashboardAIInsightCard
            insightType="daily"
            timeframe="Day"
            dateId={generateDateID(currentDate)}
            onLearnMore={handleLearnMoreInsight}
            learnMoreLoading={learnMoreLoading}
            onDismiss={handleDismissInsight}
            forceShow={!isDailyInsightDismissed}
            hasRequiredData={
              !!(
                activeDayPost?.wellnessLogs &&
                activeDayPost.wellnessLogs.length > 0 &&
                activeDayPost?.peakScoreLog?.peakScore
              )
            }
          />
        )}
      </DashboardBackground>

      <div className="mx-auto max-w-3xl" id="top-container">
        <div className=" mx-auto w-full xl:px-2">
          {/* New Activity Alerts */}
          {unlinkedActivities && unlinkedActivities.length > 0 && (
            <div className="bg-blue-300/20 sm:border sm:border-blue-700  px-8 py-8 sm:rounded-lg sm:my-4">
              <div className="flex items-center space-x-3 mb-2">
                <div>
                  <div className="flex-shrink-0 flex">
                    <PlusIcon className="h-5 w-5 text-primary" />
                    <h3 className="text-base font-semibold text-base-content ml-1">
                      Link Activities
                    </h3>
                  </div>

                  {/* <p className="text-sm text-blue-700 p-1">
                    {unlinkedActivities.length}{" "}
                    {unlinkedActivities.length !== 1
                      ? "activities"
                      : "activity"}{" "}
                    <span>waiting for training details</span>
                  </p> */}
                </div>
              </div>

              <div className="space-y-3">
                {unlinkedActivities.map((activityLog, index) => {
                  const isIgnoringActivity = ignoringActivityKeys.has(
                    getActivityDedupKey(activityLog),
                  );

                  return (
                    <div
                      key={activityLog.id || index}
                      className="bg-base-100 rounded-none md:rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {/* <div className="flex-shrink-0">
                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                          </div> */}
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-base-content">
                                New{" "}
                                <span className="font-bold">
                                  {activityLog.provider || "wearable"}
                                </span>{" "}
                                activity
                              </h4>
                            </div>
                            <div className="sm:flex sm:items-center sm:space-x-4 text-xs text-base-content/60">
                              {activityLog.duration && (
                                <span>
                                  {Math.round(activityLog.duration)} min
                                </span>
                              )}
                              <br></br>
                              {activityLog.sessionName && (
                                <span className="font-medium text-base-content/80">
                                  {activityLog.sessionName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              handleOpenActivityLinkModal(activityLog)
                            }
                            disabled={isIgnoringActivity}
                            className="bg-blue-500 hover:bg-blue-500 text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
                          >
                            Link Training
                          </button>
                          <button
                            onClick={() => handleIgnoreActivity(activityLog)}
                            disabled={isIgnoringActivity}
                            className="px-2.5 py-1.5 rounded-md text-xs font-medium border border-base-300 text-base-content/70 bg-base-200 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            title="Hide this activity"
                          >
                            {isIgnoringActivity && (
                              <span className="inline-block h-3 w-3 border-2 border-base-content/40 border-t-base-content rounded-full animate-spin" />
                            )}
                            {isIgnoringActivity ? "Ignoring..." : "Ignore"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Training Sessions */}
          <div
            className="bg-base-100 rounded-none md:rounded-lg px-4 py-4 mt-0 sm:mt-4"
            id="training-sessions"
          >
            <h3 className="text-sm font-heading px-4 mb-4">
              Training{" "}
              <span className="font-grotesk text-base-content/60">
                Sessions
              </span>
            </h3>
            <div className="px-4 mb-2">
              {activeDayPost &&
              activeDayPost.trainingLogs &&
              activeDayPost.trainingLogs.length > 0 ? (
                activeDayPost.trainingLogs.map((trainingLog, index) => {
                  const linkedActivity = getLinkedActivityForTraining(
                    trainingLog,
                    activeDayPost.activityLogs,
                  );

                  return (
                    <TrainingSessionCard
                      key={trainingLog.id || index}
                      trainingLog={trainingLog}
                      linkedActivity={linkedActivity}
                      onClick={goToTrainingLogInsights}
                      className={
                        index < activeDayPost.trainingLogs.length - 1
                          ? "mb-2"
                          : ""
                      }
                    />
                  );
                })
              ) : (
                <div className="rounded-lg text-left">
                  <p className="text-base-content/60 text-sm font-grotesk">
                    {combinedLoading
                      ? "Loading training sessions..."
                      : "No training sessions recorded yet..."}
                  </p>
                </div>
              )}
              {!combinedLoading && (
                <div className="w-full text-center">
                  <button
                    onClick={handleOpenTrainingForm}
                    className="text-sm text-base-content hover:text-base-content/70 transition-colors font-grotesk bg-base-300 px-4 py-2 rounded-md w-full mt-3"
                  >
                    Add training session
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Scheduled Sessions Section (includes regular + proposed + collection sessions) */}
          {(() => {
            // Filter regular sessions from DayPost for the current date
            const dayPostSessions = (activeDayPost?.sessions || []).filter(
              (session) =>
                session.status === "planned" || session.status === "executing",
            );

            // Merge with sessions fetched from sessions collection, dedup by ID
            const dayPostSessionIds = new Set(dayPostSessions.map((s) => s.id));
            const extraSessions = scheduledSessions.filter(
              (s) => !dayPostSessionIds.has(s.id),
            );
            const regularSessions = [...dayPostSessions, ...extraSessions];

            // Filter proposed sessions for the currently viewed date
            // Local calendar day, not toISOString(): plannedDate is a local
            // date and the UTC form shifted near midnight.
            const currentDateStr = toLocalDateString(activeDate); // YYYY-MM-DD
            const proposedForDate = proposedSessions.filter(
              (session) => session.plannedDate === currentDateStr,
            );

            const hasAnySessions =
              regularSessions.length > 0 || proposedForDate.length > 0;

            if (!hasAnySessions) return null;

            return (
              <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-4 mt-3">
                <h3 className="text-sm font-heading px-0 mb-4">
                  Scheduled{" "}
                  <span className="font-grotesk text-base-content/60">
                    Sessions
                  </span>
                </h3>
                <div className="space-y-3">
                  {/* Proposed sessions */}
                  {proposedForDate.map((session) => (
                    <div
                      key={`proposed-${session.id}`}
                      onClick={() => navigate(`/session/${session.id}`)}
                      className="flex items-center justify-between p-4 rounded-md border-1 border bg-base-200 border-base-300 hover:border-base-content/60 hover:bg-base-300 transition-colors"
                    >
                      <div>
                        <div className="">
                          {(() => {
                            const title = asDisplayNameOrNull(session.name);
                            const typeName = resolveSessionTypeName(session);
                            const primaryTitle = title || typeName || "Session";

                            return (
                              <>
                                <div className="font-semibold text-base-content font-heading">
                                  {primaryTitle}
                                </div>
                                {title && typeName && (
                                  <div className="text-xs text-base-content/60 font-geist bg-base-100 p-2 py-1 rounded-md my-1">
                                    {typeName}
                                  </div>
                                )}
                                {(session.coach?.name || session.coachID) && (
                                  <div className="text-xs text-base-content/50 font-grotesk mt-1">
                                    Set by{" "}
                                    {session.coach?.name
                                      ? `${session.coach.name}${session.coach.surname ? ` ${session.coach.surname}` : ""}`
                                      : "Coach"}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() =>
                            handleAcceptProposedSession(
                              session.id,
                              session.coachID || "",
                            )
                          }
                          disabled={
                            acceptingSessionId === session.id ||
                            rejectingSessionId === session.id
                          }
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
                        >
                          {acceptingSessionId === session.id
                            ? "Accepting..."
                            : "Accept"}
                        </button>
                        <button
                          onClick={() => navigate(`/session/${session.id}`)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-base-content/80 bg-base-300 rounded-lg hover:bg-base-300 transition-colors font-grotesk"
                        >
                          View
                        </button>
                      </div> */}
                    </div>
                  ))}

                  {/* Regular scheduled/executing sessions. Tapping the card
                      opens the session sheet (plan 3b/3c); the chevron goes
                      straight to the session page. */}
                  {regularSessions.map((session, index) => (
<div
  key={session.id || index}
  onClick={() =>
    queryUid
      ? navigate(`/session/${session.id}`)
      : setSheetSession(session)
  }
  className={`relative isolate overflow-hidden flex items-center justify-between gap-3 p-4 rounded-xl border border-l-4 cursor-pointer transition-colors bg-base-200 ${
    session.status === "executing"
      ? "border-warning/30 border-l-warning hover:border-warning/50"
      : "border-base-300 border-l-base-content/60 hover:border-base-content/60 hover:border-l-base-content/80 hover:bg-base-300"
  }`}
>
                      {/* Warning tint on an opaque base so nothing shows through behind the text */}
                      {session.status === "executing" && (
                        <span
                          className="absolute inset-0 -z-10 bg-warning/10 pointer-events-none"
                          aria-hidden
                        />
                      )}
                      <div className="space-y-2 min-w-0">
                        <div>
                          {(() => {
                            const title = asDisplayNameOrNull(session.name);
                            const typeName = resolveSessionTypeName(session);
                            const primaryTitle =
                              title ||
                              typeName ||
                              (session.status === "executing"
                                ? "Session In Progress"
                                : "Session");

                            return (
                              <>
                                <div className="font-semibold text-base-content font-heading">
                                  {primaryTitle}
                                </div>
                                {title && typeName && (
                                  <div className="text-xs text-base-content font-geist bg-base-100 px-2 py-1 mt-2 mb-0 rounded-md border">
                                    {typeName}
                                  </div>
                                )}
                                {(session.coach?.name || session.coachID) && (
                                  <div className="text-xs text-base-content/50 font-grotesk mt-1">
                                    Set by{" "}
                                    {session.coach?.name
                                      ? `${session.coach.name}${session.coach.surname ? ` ${session.coach.surname}` : ""}`
                                      : "Coach"}
                                  </div>
                                )}
                                {/* {session.description && (
                                  <div className="text-sm text-base-content mt-0">
                                    {session.description}
                                  </div>
                                )} */}
                                {session.status === "executing" && (
                                  <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-warning text-xs mt-1.5 font-grotesk">
                                    <span className="relative flex h-2 w-2 flex-shrink-0">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-warning opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2 w-2 bg-warning"></span>
                                    </span>
                                    <span className="font-semibold">
                                      In progress
                                    </span>
                                    {session.startTime && (
                                      <span className="text-base-content/60 font-geist tabular-nums">
                                        · Started{" "}
                                        {new Date(
                                          session.startTime,
                                        ).toLocaleTimeString("en-GB", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </span>
                                    )}
                                  </span>
                                )}
                                {!queryUid && canChangeSessionDate(session) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleChangeDateRequest(session);
                                    }}
                                    className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-lg border border-base-300 bg-base-100 text-xs font-grotesk text-base-content/70 hover:text-base-content hover:border-base-content/40 transition-colors"
                                  >
                                    <CalendarDaysIcon className="w-3.5 h-3.5" />
                                    Change date
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {session.status === "executing" && !queryUid ? (
                        /* Continue the live session: DaisyUI warning button,
                           ≥44px tap target, type name in the label. */
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/session/${session.id}`);
                          }}
                          aria-label="Continue session"
                          className="btn btn-warning btn-sm min-h-11 h-11 rounded-xl px-4 font-grotesk font-semibold normal-case flex-shrink-0 max-w-[45%] sm:max-w-none"
                        >
                          <PlayIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">
                            Continue
                            {(() => {
                              const typeName = resolveSessionTypeName(session);
                              return typeName ? ` · ${typeName}` : "";
                            })()}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/session/${session.id}`);
                          }}
                          aria-label="Open session"
                          className="btn btn-ghost btn-square btn-sm min-h-11 h-11 w-11 rounded-xl text-base-content/70 hover:text-base-content flex-shrink-0"
                        >
                          <ChevronRightIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {dayRecoveries.length > 0 && (
            <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-4 mt-3">
              <h3 className="text-sm font-heading px-0 mb-4">
                Recovery{" "}
                <span className="font-grotesk text-base-content/60">
                  to log
                </span>
              </h3>
              <div className="space-y-2">
                {dayRecoveries.map((item) => (
                  <RecoveryScheduleCard
                    key={item.id}
                    item={item}
                    onClick={
                      queryUid || isScheduledRecoveryLogged(item)
                        ? undefined
                        : () => {
                            setEditingRecoveryLog(null);
                            setRecoveryToLog(item);
                            setShowRecoveryForm(true);
                          }
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Unlinked completed sessions — surface them so the athlete can log
              them (create a new training log) or link to an existing one. */}
          {!queryUid &&
            (() => {
              // Merge completed sessions from the cached DayPost with those
              // fetched directly from the sessions collection for this date so
              // completed sessions surface even when the DayPost is stale or
              // doesn't yet reference them. DayPost entries are the fresher
              // source (they reflect recent links) so they win on ID collisions.
              const sessionsById = new Map<string, Session & { id: string }>();
              for (const session of completedSessions) {
                if (session?.id) sessionsById.set(session.id, session);
              }
              for (const session of activeDayPost?.sessions || []) {
                if (!session?.id) continue;
                // DayPost sessions are fresher (reflect recent links) but the
                // direct-fetch carries populated coach info, so keep the coach
                // details when the DayPost copy is missing them.
                const existing = sessionsById.get(session.id);
                sessionsById.set(session.id, {
                  ...session,
                  coach: session.coach ?? existing?.coach,
                });
              }
              const unlinkedCompletedSessions = Array.from(
                sessionsById.values(),
              ).filter(
                (session) =>
                  session.status === "complete" &&
                  !session.linkedTrainingLogID,
              );
              return (
                <UnlinkedSessionsSection
                  sessions={unlinkedCompletedSessions}
                  trainingLogs={activeDayPost?.trainingLogs || []}
                  onCreateLog={handleCreateLogForSession}
                  onLinkToLog={handleLinkSessionToLog}
                  onViewSession={(session) => setSheetSession(session)}
                  getSessionTitle={(session) =>
                    session.name?.trim() ||
                    resolveSessionTypeName(session) ||
                    "Session"
                  }
                  linkingSessionId={linkingSessionId}
                />
              );
            })()}

          {/* Events to log (Events.md §5): events on the viewed day (<= today)
              with no linked training log. */}
          {!queryUid && (
            <EventsToLogSection
              userId={currentUser?.uid}
              dateID={generateDateID(currentDate)}
              refreshKey={performancesRefreshKey}
              onLogEvent={(event) => setPerformanceFlowEvent(event)}
            />
          )}

          {/* Training Days Component - Only show for current day and athlete users */}
          {isToday(currentDate) && !userData?.casualMode && (
            <div
              className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3 cursor-pointer"
              id="training-container"
              onClick={goToTrainingInsights}
            >
              <TrainingDays
                dayPosts={dayPosts}
                targetLoad={loadGoalTarget}
                loadBalance={displayWorkloadFactor(activeDayPost?.peakScoreLog)}
                acuteLoad={activeDayPost?.peakScoreLog?.EWMA_WorkA}
              />
            </div>
          )}

          {/* Wellness Days Component - Only show for current day and athlete users */}
          {isToday(currentDate) && !userData?.casualMode && (
            <div
              className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3 cursor-pointer"
              onClick={goToWellnessInsights}
            >
              <WellnessDays
                dayPosts={dayPosts}
                acuteWellness={activeDayPost?.peakScoreLog?.wellnessA}
              />
            </div>
          )}

          {/* Sleep Days Component - Only show for current day and athlete users */}
          {isToday(currentDate) && !userData?.casualMode && (
            <div
              className="bg-base-100 rounded-none md:rounded-lg px-8 py-6 mt-3 cursor-pointer"
              id="sleep-container"
              onClick={goToSleepInsights}
            >
              <SleepDays dayPosts={dayPosts} targetHours={sleepGoalHours} />
            </div>
          )}

          {/* Streak Card + Activity Heatmap moved below Steps section */}

          {/* Cycle Tracking Component - Only show for current day and female users */}
          {isToday(currentDate) &&
            !cycleLoading &&
            userData?.gender?.toLowerCase() === "female" && (
              <div
                className="mt-3 cursor-pointer"
                onClick={() => navigate("/cycle")}
              >
                {cycleStats && cycleStats.most_recent_cycle_start_date ? (
                  <CurrentCycleCard
                    most_recent_cycle_start_date={
                      cycleStats.most_recent_cycle_start_date
                    }
                    average_cycle_length={cycleStats.average_cycle_length}
                  />
                ) : (
                  <div className="bg-base-100 rounded-none md:rounded-lg p-8 py-6 shadow-sm flex justify-between">
                    <div>
                      <div className="flex justify-between items-center">
                        <h3 className="text-sm font-heading">
                          Cycle{" "}
                          <span className="font-grotesk text-base-content/60">
                            Tracking
                          </span>
                        </h3>
                      </div>
                      <p className="text-base-content/50 text-sm">
                        No cycle logged yet.
                      </p>
                    </div>
                    <button className="p-2.5 py-1 bg-pink-500 rounded-md font-grotesk text-white font-bold text-sm">
                      Start
                    </button>
                  </div>
                )}
              </div>
            )}

          {/* Health Stats — wearable days, or a day that already has a health log */}
          {(hasConnectedWearableDevices || latestHealthLog) && (
            <div className="bg-base-100 rounded-none md:rounded-lg px-4 py-2 mt-3">
              {/* Latest Health (Recovery) */}
              <div>
                <RecoveryCard
                  latestHealthLog={latestHealthLog}
                  hlrScore={hlrScore}
                  rhrPercentDiff={healthMetricsComparison.rhrPercentDiff}
                  hrvPercentDiff={healthMetricsComparison.hrvPercentDiff}
                  respPercentDiff={healthMetricsComparison.respPercentDiff}
                  onClick={goToSleepInsights}
                  blurHLR={!isPlusOrHigher}
                />
              </div>
            </div>
          )}

          {/* Recovery Section - Only show if there are recovery logs */}
          {activeDayPost &&
            activeDayPost.recoveryLogs &&
            activeDayPost.recoveryLogs.length > 0 && (
              <div className="bg-base-100 rounded-none md:rounded-lg px-4 py-2 mt-3">
                <div className="px-4 py-2">
                  {/* Recovery Logs */}
                  <div className="mb-2">
                    {activeDayPost.recoveryLogs.map((recoveryLog, index) => (
                      <div
                        key={recoveryLog.id || index}
                        className={
                          index < activeDayPost.recoveryLogs.length - 1
                            ? "mb-3"
                            : ""
                        }
                      >
                        <RecoveryLogCard
                          log={recoveryLog}
                          onEdit={!queryUid ? handleEditRecoveryLog : undefined}
                          onDelete={
                            !queryUid ? handleDeleteRecoveryLog : undefined
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {/* Weekly Load Goal Component - Only show for current day */}
          {/* {isToday(currentDate) && (
            <WeeklyLoadGoal
              currentLoad={currentWeekLoad}
              goalLoad={weeklyLoadGoalTarget}
              dailyLoads={weeklyDailyLoads}
              className="bg-base-100 rounded-none md:rounded-lg mt-3"
            />
          )} */}

          {/* Only show Health Stats if user has connected wearable devices */}
          {hasConnectedWearableDevices ? (
            <div className="grid grid-cols-1 gap-y-3">
              {/* Wearable Daily Summary - winners per metric */}
              {winnerSteps && (
                <div
                  className="bg-base-100 px-8 py-4 mt-3 cursor-pointer shadow-sm"
                  onClick={goToDailyInsights}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex">
                        <h3 className="text-sm font-heading font-semibold mb-1 flex">
                          Steps{" "}
                        </h3>
                        <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded ml-2 font-heading">
                          {winnerSteps.provider}
                        </span>
                      </div>
                      <div className="text-4xl font-bold tracking-tight">
                        {Math.round(winnerSteps.totals.steps).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-end h-24 space-x-1">
                      {winnerSteps.stepsHistogram.hours.map((h, i) => {
                        const ratio =
                          winnerSteps.stepsHistogram.max > 0
                            ? h.value / winnerSteps.stepsHistogram.max
                            : 0;
                        const height = Math.max(2, Math.round(ratio * 80));
                        return (
                          <div
                            key={i}
                            className="w-1.5 bg-base-content/50 rounded-sm"
                            style={{ height }}
                            title={`${h.label}:00 • ${h.value} steps`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* {winnerDistance && (
                <div
                  className="bg-base-100 px-8 py-4 cursor-pointer shadow-sm"
                  onClick={goToDailyInsights}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-heading font-semibold mb-1">
                        Distance{" "}
                        <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded ml-2">
                          {winnerDistance.provider}
                        </span>
                      </h3>
                      <div className="text-4xl font-bold tracking-tight">
                        {(winnerDistance.totals.distance / 1000).toFixed(2)}
                        <span className="text-lg font-normal ml-1">km</span>
                      </div>
                    </div>
                    <div className="h-12 w-24 flex items-center justify-center">
                      <Sparkline
                        values={winnerDistance.distanceSparkValues}
                        width={96}
                        height={36}
                        strokeClassName="stroke-gray-500"
                      />
                    </div>
                  </div>
                </div>
              )} */}
              {/* {winnerCalories && (
                <div
                  className="bg-base-100 px-8 py-4 cursor-pointer shadow-sm"
                  onClick={goToDailyInsights}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-heading font-semibold mb-1">
                        Energy burned
                        <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded ml-2">
                          {winnerCalories.provider}
                        </span>
                      </h3>
                      <div className="text-4xl font-bold tracking-tight">
                        {Math.round(
                          winnerCalories.totals.calories
                        ).toLocaleString()}
                        <span className="text-lg font-normal ml-1">cal</span>
                      </div>
                    </div>
                    <div className="h-12 w-24 flex items-center justify-center">
                      <Sparkline
                        values={winnerCalories.caloriesSparkValues}
                        width={96}
                        height={36}
                        strokeClassName="stroke-gray-500"
                      />
                    </div>
                  </div>
                </div>
              )} */}
              {/* {winnerOxygen && (
                <div
                  className="bg-base-100 px-4 py-4 cursor-pointer shadow-sm"
                  onClick={goToDailyInsights}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-heading font-semibold">
                        Oxygen{" "}
                        <span className="text-xs bg-base-200 text-base-content/80 px-2 py-1 rounded">
                          {winnerOxygen.provider}
                        </span>
                      </h3>
                      <div className="text-4xl font-bold tracking-tight">
                        {Math.round(winnerOxygen.totals.oxygen)}
                        <span className="text-lg font-normal ml-1">%</span>
                      </div>
                      <p className="text-sm text-base-content/50 mt-1">Today</p>
                    </div>
                    <div className="relative">
                      <RadialProgress
                        value={Math.min(winnerOxygen.totals.oxygen, 100)}
                        size="4.5rem"
                        thickness="6px"
                        color="text-green-600"
                        text="🫁"
                        textSize="text-lg"
                      />
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          ) : (
            !queryUid &&
            isToday(currentDate) && (
              <div className="bg-base-100 rounded-none md:rounded-lg px-4 py-4 mt-3">
                <h3 className="text-sm font-heading px-4 mb-2">
                  Wearable{" "}
                  <span className="font-grotesk text-base-content/60">
                    Data
                  </span>
                </h3>
                <div className="px-4 mb-2">
                  <div className="rounded-lg text-left">
                    <p className="text-base-content/60 text-sm font-grotesk">
                      Connect your wearable device to automatically track sleep,
                      health, steps, distance, calories, and more.
                    </p>
                  </div>
                  <div className="w-full text-center">
                    <button
                      onClick={() => navigate("/settings/wearable")}
                      className="text-sm text-base-content hover:text-base-content/70 transition-colors font-grotesk bg-base-300 px-4 py-2 rounded-md w-full mt-3"
                    >
                      Connect wearable
                    </button>
                  </div>
                </div>
              </div>
            )
          )}

          {/* Streak Card - Show for all non-casual users (premium + regular) */}
          {isToday(currentDate) && !userData?.casualMode && (
            <>
              <StreakCard userId={queryUid || currentUser?.uid || ""} />
            </>
          )}

          {/* Activity Heatmap - Only show for current day, athlete users, and premium users */}
          {isToday(currentDate) && !userData?.casualMode && !isDefault && (
            <ActivityHeatmap
              userId={queryUid || currentUser?.uid || ""}
              onClick={() => {
                navigate(
                  `/heatmap?userId=${queryUid || currentUser?.uid || ""}`,
                );
              }}
            />
          )}

          {/* Upgrade Pricing Component - Only show for free users */}
          {!isPlusOrHigher && isToday(currentDate) && !queryUid && (
            <UpgradePricingCard
              userCountry={userData?.country}
              className="mt-6 sm:mb-6 pb-24"
            />
          )}
        </div>
        <BottomNavBar></BottomNavBar>
      </div>
      {/* Floating Button - Always show the add button unless viewing another user's dashboard */}
      {!queryUid && (
        <>
          {/* Blur backdrop - behind the button */}
          {showDesktopLogMenu && (
            <div
              className="fixed inset-0 z-40 backdrop-blur-sm bg-black/20"
              onClick={() => setShowDesktopLogMenu(false)}
            />
          )}
          {/* Oly AI chat floating button (left side)
          <div className="fixed left-12 z-50 sm:bottom-12 bottom-[106px]">
            <button
              aria-label="Open Oly AI chat"
              onClick={() => setShowOlyChat(true)}
              className="rounded-full h-14 w-14 shadow-md shadow-black/40 hover:shadow-sm active:shadow-sm hover:opacity-90 focus:outline-none flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 text-white transition-shadow"
              title="Talk to Oly"
            >
              <SparklesIcon className="h-6 w-6" />
            </button>
          </div> */}
          <div className="fixed right-12 z-50 sm:bottom-12 bottom-[106px]">
            {/* Dropdown menu (opens upward) */}
            {showDesktopLogMenu && (
              <div className="absolute bottom-full right-0 mb-2">
                <div className="bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[180px]">
                  {[
                    { type: "training" as LogType, label: "Training" },
                    { type: "recovery" as LogType, label: "Recovery" },
                    { type: "negativeInfluences" as LogType, label: "Negative Influences" },
                    { type: "niggle" as LogType, label: "Niggle" },
                    { type: "performance" as LogType, label: "Performance" },
                    ...((dayPostData?.healthLogs || []).length === 0 &&
                    !userData?.casualMode
                      ? [{ type: "health" as LogType, label: "Health Stats" }]
                      : []),
                    { type: "note" as LogType, label: "Note" },
                    ...((dayPostData?.wellnessLogs || []).length === 0 &&
                    !userData?.casualMode
                      ? [{ type: "wellness" as LogType, label: "Wellness" }]
                      : []),
                  ].map((item, index, arr) => (
                    <button
                      key={item.type}
                      onClick={() => handleSelectLogType(item.type)}
                      className={`w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors ${
                        index < arr.length - 1 ? "border-b border-base-300" : ""
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              aria-label="Add log"
              onClick={handleOpenAddLogModal}
              className="rounded-full h-14 w-14 shadow-md shadow-black/40 hover:shadow-sm active:shadow-sm hover:opacity-90 focus:outline-none flex items-center justify-center bg-primary text-white transition-shadow"
            >
              <PlusIcon className="h-6 w-6" />
            </button>
          </div>
          {/* <AgentInput /> */}
        </>
      )}
      {/* Oly AI Chat Dialog */}
      <OlyChat
        isOpen={showOlyChat}
        onClose={() => {
          setShowOlyChat(false);
          // Oly may have added training/recovery/session/etc. logs during
          // the call, so re-fetch the dashboard's day posts to pick them up.
          void fetchDayPostsData(false);
        }}
      />
      {/* Wellness Log Form Modal */}
      <AddWellnessLogForm
        open={showWellnessForm}
        onClose={handleCloseWellnessForm}
        onSubmit={handleWellnessSubmit}
        dateID={generateDateID(currentDate)}
        editMode={false}
        transparent={false}
        trainingWarning={true}
        onGoBackToPreviousDay={goToPreviousDay}
      />
      {/* Recovery Log Form Modal */}
      <AddRecoveryLogForm
        open={showRecoveryForm}
        onClose={handleCloseRecoveryForm}
        onSubmit={handleRecoverySubmit}
        dateID={
          recoveryToLog
            ? recoveryToLog.plannedDate.replace(/-/g, "")
            : generateDateID(currentDate)
        }
        editMode={!!editingRecoveryLog}
        initialData={
          editingRecoveryLog?.data ||
          (recoveryToLog
            ? {
                selectedRecovery: [recoveryToLog.modality],
                notes: recoveryToLog.notes,
              }
            : undefined)
        }
        logId={editingRecoveryLog?.id}
      />
      <AddNegativeInfluencesLogForm
      open={showNegativeInfluencesForm}
      onClose={handleCloseNegativeInfluencesForm}
      onSubmit={handleNegativeInfluencesSubmit}
      dateID={generateDateID(currentDate)}
      editMode={false}
    />
      {/* Niggle Log Form Modal */}
      <AddNiggleLogForm
        open={showNiggleForm}
        onClose={handleCloseNiggleForm}
        onSubmit={handleNiggleSubmit}
        dateID={generateDateID(currentDate)}
        editMode={false}
        linkedTrainingLogID={linkedTrainingLogID}
      />
      {/* Performance — the five-step flow (PerformanceLog.md §3) */}
      <PerformanceFlow
        open={showPerformanceForm}
        onClose={handleClosePerformanceForm}
        dateID={generateDateID(currentDate)}
        onSaved={async () => {
          invalidateStreakCache();
          setPerformancesRefreshKey((k) => k + 1);
          if (isDateBeyondCachedRange) {
            await fetchOnDemandData(currentDate);
          } else {
            await fetchDayPostsData(false);
          }
          await refreshSessionsForActiveDate();
        }}
      />
      {/* Note Log Form Modal */}
      <AddNoteLogForm
        open={showNoteForm}
        onClose={handleCloseNoteForm}
        onSubmit={handleNoteSubmit}
        dateID={generateDateID(currentDate)}
        editMode={false}
      />
      {/* Health Log Form Modal */}
      <AddHealthLogForm
        open={showHealthForm}
        onClose={handleCloseHealthForm}
        onSubmit={handleHealthSubmit}
        dateID={generateDateID(currentDate)}
        editMode={false}
      />
      {/* Training Log Form (rebuilt flow) */}
      <TrainingLogForm
        open={showTrainingFormNew}
        onClose={() => {
          setShowTrainingFormNew(false);
          setTrainingFormSessionId(null);
        }}
        context={{
          dateID: generateDateID(currentDate),
          ...(trainingFormSessionId
            ? { sessionId: trainingFormSessionId }
            : {}),
        }}
        onSaved={async () => {
          invalidateStreakCache();
          if (isDateBeyondCachedRange) {
            await fetchOnDemandData(currentDate);
          } else {
            await fetchDayPostsData(false);
          }
          // Refresh the direct-fetch fallback so a session that just had a log
          // created for it drops out of the "Sessions to log" list.
          await refreshSessionsForActiveDate();
        }}
      />
      {/* Performance flow seeded from an "Events to log" row */}
      <PerformanceFlow
        open={!!performanceFlowEvent}
        onClose={() => setPerformanceFlowEvent(null)}
        dateID={generateDateID(currentDate)}
        seed={
          performanceFlowEvent ? { eventId: performanceFlowEvent.id } : undefined
        }
        onSaved={async () => {
          invalidateStreakCache();
          setPerformancesRefreshKey((k) => k + 1);
          if (isDateBeyondCachedRange) {
            await fetchOnDemandData(currentDate);
          } else {
            await fetchDayPostsData(false);
          }
          await refreshSessionsForActiveDate();
        }}
      />
      {/* Session bottom sheet (plan 3b/3c): view, link, change date,
          delete / reject a session tapped on the day view. */}
      <SessionBottomSheet
        isOpen={!!sheetSession}
        onClose={() => setSheetSession(null)}
        session={sheetSession}
        title={
          sheetSession
            ? asDisplayNameOrNull(sheetSession.name) ||
              resolveSessionTypeName(sheetSession) ||
              "Session"
            : ""
        }
        typeName={sheetSession ? resolveSessionTypeName(sheetSession) : null}
        ownerUserId={queryUid || currentUser?.uid}
        canEdit={!queryUid}
        onOpenSession={(session) => {
          setSheetSession(null);
          navigate(`/session/${session.id}`);
        }}
        onLinkToLog={handleLinkFromSheet}
        onChangeDate={handleChangeDateRequest}
        onDelete={handleDeleteSessionFromSheet}
        onReject={handleRejectSessionFromSheet}
      />
      {linkModalSession && (
        <SessionLinkModal
          isOpen={!!linkModalSession}
          onClose={() => setLinkModalSession(null)}
          session={linkModalSession}
          trainingLogs={activeDayPost?.trainingLogs || []}
          onLinkToLog={handleLinkSessionToLog}
          onCreateLog={handleCreateLogForSession}
          getSessionTitle={(session) =>
            session.name?.trim() || resolveSessionTypeName(session) || "Session"
          }
        />
      )}
      {dateChangeSession && (
        <EditSessionModal
          isOpen={!!dateChangeSession}
          onClose={() => setDateChangeSession(null)}
          session={dateChangeSession}
          dateOnly
          onSessionUpdated={handleSessionDateChanged}
        />
      )}
      {/* Add Log Modal */}
      <AddLogModal
        open={addLogModalOpen}
        onClose={handleCloseAddLogModal}
        onSelectLogType={handleSelectLogType}
        dateID={generateDateID(currentDate)}
        existingWellnessLogs={dayPostData?.wellnessLogs || []}
        existingHealthLogs={dayPostData?.healthLogs || []}
      />
      {/* Activity Link Modal */}
      {selectedActivity && (
        <ActivityLinkModal
          isOpen={showActivityLinkModal}
          onClose={handleCloseActivityLinkModal}
          activity={selectedActivity}
          unlinkedTrainingLogs={unlinkedTrainingLogs}
          dateID={generateDateID(currentDate)}
          onSuccess={handleActivityLinkSuccess}
        />
      )}
      {/* Dashboard Information Dialog */}
      <DashboardExportDialog
        isOpen={showDashboardExport}
        onClose={() => setShowDashboardExport(false)}
        peakScore={peakScore}
        wellnessScore={wellnessScore}
        totalLoad={totalLoad}
        sleepDurationHours={sleepDuration / 3600}
        sleepPercentage={Math.min(
          (sleepDuration / 3600 / (userData?.sleepGoal ?? 8)) * 100,
          100,
        )}
        totalLoadPercentage={Math.min(
          (totalLoad / (userData?.loadGoal ?? 400)) * 100,
          100,
        )}
      />

      {showDashboardInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60] sm:p-4">
          <div className="bg-base-100 rounded-none sm:rounded-lg max-w-2xl w-full h-full sm:h-auto sm:max-h-[80vh] overflow-y-auto sm:mx-8">
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading font-semibold text-base-content">
                  Dashboard Guide
                </h3>
                <button
                  onClick={() => setShowDashboardInfoDialog(false)}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <div>
                  <h4 className="font-bold text-base-content mb-2">
                    Peak Score
                  </h4>
                  <p className="mb-2">
                    Your <strong>Peak Score</strong> is a comprehensive
                    readiness metric (0–110) that combines wellness, sleep,
                    soreness, illness, training load and frequency, as well as
                    objective cardiorespiratory data to show how prepared you
                    are for training.
                  </p>
                  <div className="space-y-2 ml-4">
                    {/* Color gradient bar */}
                    <div
                      className="h-3 rounded-full w-full"
                      style={{
                        background:
                          "linear-gradient(to right, #ef4444 0%, #ef4444 27.27%, #f97316 27.27%, #f97316 54.55%, #22c55e 54.55%, #22c55e 72.73%, #0096c7 72.73%, #0096c7 90.91%, #DAA520 90.91%, #DAA520 100%)",
                      }}
                    />
                    {/* Score markers */}
                    <div className="relative text-xs text-base-content/60 font-medium">
                      <span className="absolute" style={{ left: "0%" }}>
                        0
                      </span>
                      <span className="absolute" style={{ left: "27.27%" }}>
                        30
                      </span>
                      <span className="absolute" style={{ left: "54.55%" }}>
                        60
                      </span>
                      <span className="absolute" style={{ left: "72.73%" }}>
                        80
                      </span>
                      <span className="absolute" style={{ left: "90.91%" }}>
                        100
                      </span>
                      <span
                        className="absolute"
                        style={{ left: "100%", transform: "translateX(-100%)" }}
                      >
                        110
                      </span>
                    </div>
                    <br></br>
                    {/* Color descriptions */}
                    <div className="grid grid-cols-1 gap-1.5 text-xs mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#DAA520] flex-shrink-0"></div>
                        <span>
                          <strong>Golden Zone (&gt;100):</strong> Ideal
                          conditions for possible personal best today.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#0096c7] flex-shrink-0"></div>
                        <span>
                          <strong>Excellent Readiness (80-100):</strong>
                          Optimal for high-intensity training
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#22c55e] flex-shrink-0"></div>
                        <span>
                          <strong>Good Readiness for building (60-79):</strong>
                          Suitable for all training but monitor warm up.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#f97316] flex-shrink-0"></div>
                        <span>
                          <strong>Average Readiness (30-59):</strong>
                          Room for improvement in wellness & training load
                          balance. If training, monitor warm up and possibly
                          reduce training load and intensity today.
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#ef4444] flex-shrink-0"></div>
                        <span>
                          <strong>Strong Caution (0-29):</strong> Low readiness,
                          prioritise recovery and rest today
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-base-content mb-2">
                    Wellness Score
                  </h4>
                  <p>
                    The Wellness score is your self-reported snapshot of today's
                    sleep quality, duration, mood, stress, recovery, physical
                    perception and health, relative to a factor of your last 7
                    days and 28 day averages. This is the foundation of your
                    Peak Score and helps identify patterns in your well-being.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-base-content mb-2">
                    Training Load
                  </h4>
                  <p>
                    Shows your total training load for the day. Training load
                    combines duration, intensity and session load modifiers to
                    quantify your training stress. Monitor this to balance
                    training and recovery.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-base-content mb-2">Sleep</h4>
                  <p>
                    Displays your sleep duration. Quality sleep is essential for
                    recovery and performance. The circle fills based on your
                    sleep goal.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-base-content mb-2">
                    Heart Lung Recovery index (HLR)
                  </h4>
                  <p>
                    The ratio of Resting Heart Rate (RHR), Heart Rate
                    Variability (HRV) and Respiratory Rate (RR). A balanced HLR
                    indicates your cardiorespiratory system is adapting
                    appropriately for your training. A low HLR may indicate your
                    cardiorespiratory system is not recovering at the same rate
                    as the stress your body is experiencing from training and
                    lifestyle.
                  </p>
                </div>

                <p className="text-xs text-base-content/70 italic">
                  Tip: Click on any metric on the dashboard to view detailed
                  insights and trends over time.
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDashboardInfoDialog(false)}
                  className="px-4 py-2 bg-neutral text-neutral-content rounded-md hover:bg-neutral/90 font-grotesk transition-colors font-medium"
                >
                  Okay, got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
